import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Download, Trash2 } from 'lucide-react';
import { useT } from './i18n/index';
import type { Box, Arrow, VisibleLevels, Resource, Area, Department } from './types/index';
import type { TimelinePreset, ExportRowPreset } from './types/ganttUtils';
import { buildGanttSVGString } from './lib/svgExport';
import { is24hEndFromStart, convertDurationUnit, assignBoxesToAreas, buildExpandedGanttRows, calcAreaCompletion } from './lib/gantt';

// ── Helper ─────────────────────────────────────────────────────────────────

// Fixed width per column for the optional "Show start/end" columns (one for Start,
// one for End) — unlike ganttLabelWidth, not user-resizable (keeps every row-type
// branch below simple: always exactly DATE_COL_W * 2 extra width to account for).
const DATE_COL_W = 90;

// ── Props ──────────────────────────────────────────────────────────────────

export interface GanttPanelProps {
  ganttTasks: Array<Omit<Box, 'duration'> & { start: number; duration: number }>;
  /** Each subitem's own scheduled position (see lib/gantt.ts's calcSubitemGanttTasks),
   *  already shifted to line up under its parent's actual bar. Rendered as extra
   *  indented rows directly under the parent's row when viewState.showSubitemsInGantt
   *  is on — never in the resource-filtered/summary views. */
  subitemGanttTasks: Array<Omit<Box, 'duration'> & { start: number; duration: number }>;
  arrows: Arrow[];
  boxes: Box[];
  /** For Area-based "toplevel" grouping (see lib/gantt.ts's assignBoxesToAreas) —
   *  never used for anything else here. */
  areas: Area[];
  /** For "filter by category" — a box's category is its departmentColor field,
   *  matched against Department.color to get a displayable/filterable name. */
  departments: Department[];
  title: string;
  resources: Resource[];

  // Scheduling configuration — how the Gantt engine computes start/duration/positions.
  scheduleSettings: {
    durationUnit: string;
    ganttStartDate: Date;
    workHoursOnly: boolean;
    onWorkHoursOnlyChange: (v: boolean) => void;
    /** Display-only: show all 24 hours per day (hours mode) instead of only the configured Workday hours. Does not affect scheduling. */
    hideNonWorkingHours: boolean;
    onHideNonWorkingHoursChange: (v: boolean) => void;
    workDays: number[];
    fullDaySchedule: boolean;
    /** Workday start hour (0-23), default 9. Ignored when fullDaySchedule is on. */
    dayStartHour: number;
    /** Workday length in hours (end hour - dayStartHour), default 8. Ignored when fullDaySchedule is on. */
    hoursPerDay: number;
    ganttNonWorkingDays: string[];
  };

  // Persisted view/UI state — each independently saved and restored per flowchart.
  viewState: {
    ganttOrder: number[];
    onGanttOrderChange: (order: number[]) => void;
    initialLabelWidth?: number | null;
    onLabelWidthChange: (width: number) => void;
    timelineView?: { start: string; end: string } | null;
    onTimelineViewChange: (v: { start: string; end: string } | null) => void;
    /** Named, saved timeline ranges — shared with every collaborator (see
     *  types/ganttUtils.ts's TimelinePreset). */
    timelinePresets: TimelinePreset[];
    onAddTimelinePreset: (name: string, start: string, end: string) => void;
    onUpdateTimelinePreset: (id: string, patch: Partial<Omit<TimelinePreset, 'id'>>) => void;
    onDeleteTimelinePreset: (id: string) => void;
    initialVisibleLevels?: VisibleLevels | null;
    onVisibleLevelsChange: (levels: VisibleLevels) => void;
    initialZoom?: number | null;
    onZoomChange: (zoom: number) => void;
    /** Export setting: when true (and a timeline range is set), rows with no visible bar in that range are left out of the export. */
    onlyVisibleBars: boolean;
    onOnlyVisibleBarsChange: (v: boolean) => void;
    /** "Filter by resource" selection — persisted to localStorage (per flowchart) by the caller so it survives switching away from and back to the Gantt view. */
    resourceFilter: string[];
    onResourceFilterChange: (filter: string[]) => void;
    /** "Filter by category" selection — same convention as resourceFilter above
     *  (localStorage-only, personal viewing preference). List of Department names. */
    categoryFilter: string[];
    onCategoryFilterChange: (filter: string[]) => void;
    /** Gantt-only bar duration-label display unit (hours/days/weeks) — independent of
     *  scheduleSettings.durationUnit (which drives actual scheduling/positioning).
     *  Defaults to scheduleSettings.durationUnit until the user explicitly picks one. */
    ganttDurationDisplay: string;
    onGanttDurationDisplayChange: (unit: string) => void;
    /** Show subitems as indented rows under their parent's row (display-only, never
     *  affects scheduling; always off in the resource-filtered/summary views). */
    showSubitemsInGantt: boolean;
    onShowSubitemsInGanttChange: (v: boolean) => void;
    /** Group boxes into Area-based "toplevel" rows (display-only, never affects
     *  scheduling; always off in the resource-filtered/summary views). */
    showAreaGroupsInGantt: boolean;
    onShowAreaGroupsInGanttChange: (v: boolean) => void;
    /** Show regular box rows at all (display-only, never affects scheduling; always
     *  on in the resource-filtered/summary views) — off + showAreaGroupsInGantt on
     *  shows only the Area toplevel bars. */
    showRegularBoxesInGantt: boolean;
    onShowRegularBoxesInGanttChange: (v: boolean) => void;
    /** Show a column right after each task's title with its start/end text (display-
     *  only, never affects scheduling). Empty for rows with no single real box id
     *  (subitem rows, Area-header rows, resource-summary rows) — see startEndText. */
    showStartEndColumnInGantt: boolean;
    onShowStartEndColumnInGanttChange: (v: boolean) => void;
    /** "Filtered Export": when on, a checkbox appears next to every top-level row and
     *  only checked rows are included when exporting as PNG/SVG — combinable with
     *  onlyVisibleBars above. Mode + selection are the caller's localStorage-only
     *  working state (like resourceFilter); exportRowPresets below are named, saved
     *  selections shared via the DB (see types/ganttUtils.ts's ExportRowPreset). */
    filteredExportEnabled: boolean;
    onFilteredExportEnabledChange: (v: boolean) => void;
    exportSelectedBoxIds: number[];
    onExportSelectedBoxIdsChange: (ids: number[]) => void;
    exportRowPresets: ExportRowPreset[];
    onAddExportRowPreset: (name: string, boxIds: number[], timelineRange?: { start: string; end: string } | null) => void;
    onUpdateExportRowPreset: (id: string, patch: Partial<Omit<ExportRowPreset, 'id'>>) => void;
    onDeleteExportRowPreset: (id: string) => void;
  };

  onClose: () => void;

  calcEarliestStartOffset: (box: { id: number; earliestStart?: string }) => number;
  endOffsetToDate: (end: number, asPosition?: boolean) => Date;
  /** Real clock start of every alwaysOn/is24_5 task, walking the whole is24h ancestor
   *  chain in real time (same computation as getAlwaysOnClockStart, used for each
   *  box's own displayed start/end label) — the source of truth for hours-mode Gantt
   *  positioning of is24h tasks and their successors. */
  is24hRealStarts: Map<number, Date>;
  /** Each top-level task's own start/end display text — same strings the canvas's
   *  start/end badges show (useGanttSchedule.ts's getStart/getEnd, precomputed once
   *  there since those functions aren't valid for subitem ids). Used by the "Show
   *  start/end column" view option, in both the live view and (via exportGanttAs)
   *  the PNG/SVG export. */
  startEndText: Map<number, { start: string; end: string }>;
  /** Right-click "Show in Flowplan" on a bar: switch back to the canvas, select this box, and center on it. */
  onShowInFlowplan: (boxId: number) => void;
  /** Right-click "Select specific resource times": set which cells (hours/days) of a task a
   *  specific resource is active for. `hours: null` clears the override. */
  onResourceHoursChange: (boxId: number, resource: string, hours: boolean[] | null) => void;
  /** Critical path, pre-computed by FlowchartBuilder via lib/gantt's calcCriticalPath — the
   *  same computation the flowchart canvas uses, so both views always agree. Passed in rather
   *  than recomputed here so the Gantt view's resource filter can't affect it (critical path
   *  is a project-wide property, not scoped to whichever resource is currently shown). */
  criticalPath: { boxIds: Set<number>; arrowIds: Set<number> };
}

// ── Component ──────────────────────────────────────────────────────────────

export default function GanttPanel(props: GanttPanelProps) {
  const { t: tr } = useT();
  const {
    ganttTasks, subitemGanttTasks, arrows, boxes, areas, departments, title,
    resources,
    scheduleSettings: {
      durationUnit,
      ganttStartDate,
      workHoursOnly, onWorkHoursOnlyChange, workDays = [1,2,3,4,5],
      hideNonWorkingHours, onHideNonWorkingHoursChange,
      fullDaySchedule,
      dayStartHour, hoursPerDay,
      ganttNonWorkingDays,
    },
    viewState: {
      ganttOrder, onGanttOrderChange,
      initialLabelWidth,
      onLabelWidthChange,
      timelineView,
      onTimelineViewChange,
      timelinePresets, onAddTimelinePreset, onUpdateTimelinePreset, onDeleteTimelinePreset,
      initialVisibleLevels,
      onVisibleLevelsChange,
      initialZoom,
      onZoomChange,
      onlyVisibleBars: exportOnlyVisibleBars, onOnlyVisibleBarsChange: setExportOnlyVisibleBars,
      resourceFilter, onResourceFilterChange: setResourceFilter,
      categoryFilter, onCategoryFilterChange: setCategoryFilter,
      ganttDurationDisplay, onGanttDurationDisplayChange: setGanttDurationDisplay,
      showSubitemsInGantt, onShowSubitemsInGanttChange: setShowSubitemsInGantt,
      showAreaGroupsInGantt, onShowAreaGroupsInGanttChange: setShowAreaGroupsInGantt,
      showRegularBoxesInGantt, onShowRegularBoxesInGanttChange: setShowRegularBoxesInGantt,
      showStartEndColumnInGantt, onShowStartEndColumnInGanttChange: setShowStartEndColumnInGantt,
      filteredExportEnabled, onFilteredExportEnabledChange: setFilteredExportEnabled,
      exportSelectedBoxIds, onExportSelectedBoxIdsChange: setExportSelectedBoxIds,
      exportRowPresets, onAddExportRowPreset, onUpdateExportRowPreset, onDeleteExportRowPreset,
    },
    onClose,
    calcEarliestStartOffset, endOffsetToDate,
    is24hRealStarts, startEndText,
    onShowInFlowplan,
    onResourceHoursChange,
    criticalPath,
  } = props;

  // "Show start/end" columns — one shared cell renderer so every row type (including
  // the calendar-header spacer rows) agrees on width/placement. Two separate columns
  // (Start, then End), each its own call to this. Pass a real task id to show its
  // text (only present in startEndText for genuine top-level tasks — see that prop's
  // own comment); omit it (or pass an id with no entry, e.g. a subitem) to render an
  // empty cell of the same width, purely so the bars-cell stays aligned underneath.
  const renderDateCell = (taskId: number | undefined, field: 'start' | 'end') => {
    if (!showStartEndColumnInGantt) return null;
    const text = taskId !== undefined ? startEndText.get(taskId)?.[field] : undefined;
    return (
      <div
        className="truncate"
        title={text}
        style={{ width: DATE_COL_W, flexShrink: 0, alignSelf: 'stretch', display: 'flex', alignItems: 'center', fontSize: 12, color: '#6b7280', padding: '0 4px', overflow: 'hidden', borderLeft: '1px solid #f1f5f9' }}
      >
        {text}
      </div>
    );
  };

  // ── Local state ───────────────────────────────────────────────────────────

  const [ganttZoom, setGanttZoom] = useState<number>(initialZoom ?? 1);
  const ganttScrollRef = useRef<HTMLDivElement>(null);
  const ganttPinchRef = useRef<{ dist: number } | null>(null);
  const ganttTasksContainerRef = useRef<HTMLDivElement | null>(null);
  const ganttSortedTasksRef = useRef<Array<Omit<Box, 'duration'> & { start: number; duration: number }>>([]);
  const ganttDragFromRef = useRef<number | null>(null);
  const ganttDropIdxRef = useRef<number | null>(null);
  // Set by renderGanttSection (only the section that actually has allowDrag=true
  // populates a non-empty array here) — lets startGanttDrag/onMove below translate
  // a pixel position in the RENDERED row list (which includes interleaved subitem/
  // Area-header rows whenever those are shown) back into an index in the top-level-
  // only ganttOrder space that onUp actually reorders. See its own declaration
  // comment inside renderGanttSection for why this translation exists at all.
  const expandedIndexForTopLevelIndexRef = useRef<number[]>([]);

  const [ganttDragIdx, setGanttDragIdx] = useState<number | null>(null);
  const [ganttDropIdx, setGanttDropIdx] = useState<number | null>(null);
  // Drag-to-reorder for "one summary row per resource" — separate from the task-row
  // drag above (different array, different id type), same mouse-drag mechanics.
  const resourceRowsContainerRef = useRef<HTMLDivElement | null>(null);
  const resourceDragFromRef = useRef<number | null>(null);
  const resourceDropIdxRef = useRef<number | null>(null);
  const [resourceDragIdx, setResourceDragIdx] = useState<number | null>(null);
  const [resourceDropIdx, setResourceDropIdx] = useState<number | null>(null);
  const [selectedGanttTask, setSelectedGanttTask] = useState<number | null>(null);
  const [barContextMenu, setBarContextMenu] = useState<{ taskId: number; top: number; left: number; resource: string | null } | null>(null);
  const [editingResourceCell, setEditingResourceCell] = useState<{ taskId: number; resource: string } | null>(null);


  const [showGanttExportMenu, setShowGanttExportMenu] = useState(false);
  const [ganttExportMenuPos, setGanttExportMenuPos] = useState({ top: 0, left: 0 });

  // Pure geometry, not scheduling — cheap enough to recompute on every boxes/areas
  // change without its own fingerprint-gating (unlike the scheduling-heavy memos in
  // useGanttSchedule.ts).
  const areaByBoxId = useMemo(() => assignBoxesToAreas(boxes, areas), [boxes, areas]);

  const [showAllGanttDeps, setShowAllGanttDeps] = useState<boolean>(false);
  const [showCriticalPath, setShowCriticalPath] = useState<boolean>(false);
  const [showTimelinePopup, setShowTimelinePopup] = useState<boolean>(false);
  const [timelinePopupPos, setTimelinePopupPos] = useState({ top: 0, left: 0 });
  const [showViewPopup, setShowViewPopup] = useState<boolean>(false);
  const [viewPopupPos, setViewPopupPos] = useState({ top: 0, left: 0 });
  const [showTimelineDatesPopup, setShowTimelineDatesPopup] = useState<boolean>(false);
  const [timelineDatesPopupPos, setTimelineDatesPopupPos] = useState({ top: 0, left: 0 });
  const [viewStartInput, setViewStartInput] = useState<string>('');
  const [viewEndInput, setViewEndInput] = useState<string>('');
  const [newPresetName, setNewPresetName] = useState<string>('');
  const [showResourceFilterPopup, setShowResourceFilterPopup] = useState<boolean>(false);
  const [resourceFilterPopupPos, setResourceFilterPopupPos] = useState({ top: 0, left: 0 });
  const [showCategoryFilterPopup, setShowCategoryFilterPopup] = useState<boolean>(false);
  const [categoryFilterPopupPos, setCategoryFilterPopupPos] = useState({ top: 0, left: 0 });
  const [showFilteredExportPopup, setShowFilteredExportPopup] = useState<boolean>(false);
  const [filteredExportPopupPos, setFilteredExportPopupPos] = useState({ top: 0, left: 0 });
  const [newExportPresetName, setNewExportPresetName] = useState<string>('');
  // When a saved export-row preset is applied, this holds the row order it was saved
  // with — used by `displayOrder` (near sortedTasks) to show the Gantt itself in that
  // order too, WITHOUT touching the live, shared `ganttOrder` (applying a preset must
  // not silently reorder the whole Gantt for every collaborator) — it only reorders
  // this session's own rendering, and only while "Filtered Export" is on. Cleared by
  // "select all/none" (a wholesale selection change makes a saved order meaningless)
  // and by any explicit reorder action (manual drag, auto-sort), which should simply
  // win outright rather than have this silently reassert itself on the next render.
  const [exportOrderOverride, setExportOrderOverride] = useState<number[] | null>(null);
  // Same idea as exportOrderOverride, for the "Set Timeline Dates" crop: a preset
  // saved while a narrow timeline range was active would otherwise export (and
  // show on screen) blank, bar-less rows for every selected task outside whatever
  // timeline range happens to be the CURRENT shared one when someone else applies
  // it later — confusing, since "export exactly these tasks" is the whole point of
  // a row preset. Restoring the SAVED range as a local, mode-scoped override (same
  // non-negotiable as exportOrderOverride) avoids that without ever overwriting the
  // shared, DB-persisted timelineView — applying a preset must not silently reclip
  // the Gantt for every other collaborator currently looking at it.
  const [exportTimelineOverride, setExportTimelineOverride] = useState<{ start: string; end: string } | null>(null);
  // "Collapse to one summary row per resource" — shows when each filtered resource is
  // active across ALL its tasks, instead of a section with one bar per task.
  const [resourceSummaryMode, setResourceSummaryMode] = useState<boolean>(false);
  // resourceFilter/setResourceFilter come from props.viewState (destructured above) — controlled
  // by FlowchartBuilder so the selection persists (via localStorage) across mount/unmount cycles.

  const defaultLevels = (unit: string): VisibleLevels => {
    if (unit === 'weeks') return { quarters: true, months: true, weeks: true, days: false, hours: false };
    if (unit === 'hours') return { quarters: false, months: true, weeks: false, days: true, hours: true };
    return { quarters: false, months: true, weeks: true, days: true, hours: false };
  };

  const [visibleLevels, setVisibleLevelsState] = useState<VisibleLevels>(() =>
    initialVisibleLevels ?? defaultLevels(durationUnit)
  );

  const setVisibleLevels = (levels: VisibleLevels) => {
    setVisibleLevelsState(levels);
    onVisibleLevelsChange(levels);
  };

  // Always-fresh ref so the durationUnit effect can read the latest prop value
  // without needing it in the dependency array.
  const initialVisibleLevelsRef = useRef(initialVisibleLevels);
  useEffect(() => { initialVisibleLevelsRef.current = initialVisibleLevels; });

  // Sync visible levels from prop when it changes after mount (e.g. flowchart switch, async load).
  // Does NOT call onVisibleLevelsChange — value already comes from DB.
  const prevVisibleLevelsPropRef = useRef(initialVisibleLevels);
  useEffect(() => {
    if (initialVisibleLevels === prevVisibleLevelsPropRef.current) return;
    prevVisibleLevelsPropRef.current = initialVisibleLevels;
    if (initialVisibleLevels != null) {
      setVisibleLevelsState(initialVisibleLevels);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVisibleLevels]);

  // Reset to defaults when durationUnit changes (after mount) and no saved levels are present.
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) { isMountedRef.current = true; return; }
    if (!initialVisibleLevelsRef.current) {
      setVisibleLevels(defaultLevels(durationUnit));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationUnit]);

  const [ganttLabelWidth, setGanttLabelWidth] = useState<number>(initialLabelWidth ?? 192);

  // Sync label width from prop when it changes after mount (e.g. async load completing).
  const prevLabelWidthPropRef = useRef(initialLabelWidth);
  useEffect(() => {
    if (initialLabelWidth === prevLabelWidthPropRef.current) return;
    prevLabelWidthPropRef.current = initialLabelWidth;
    if (initialLabelWidth != null) {
      setGanttLabelWidth(initialLabelWidth);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLabelWidth]);
  const handleGanttLabelResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = ganttLabelWidth;
    const onMouseMove = (ev: MouseEvent) => {
      setGanttLabelWidth(Math.max(100, Math.min(500, startWidth + ev.clientX - startX)));
    };
    const onMouseUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      const finalWidth = Math.max(100, Math.min(500, startWidth + ev.clientX - startX));
      onLabelWidthChange(finalWidth);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [ganttLabelWidth, onLabelWidthChange]);

  // ── Scroll / zoom effects ─────────────────────────────────────────────────

  // Sync zoom from prop when it changes after mount (e.g. async load completing).
  const prevZoomPropRef = useRef(initialZoom);
  useEffect(() => {
    if (initialZoom === prevZoomPropRef.current) return;
    prevZoomPropRef.current = initialZoom;
    if (initialZoom != null) {
      setGanttZoom(initialZoom);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialZoom]);

  // Debounced save of zoom — wheel/pinch fire rapidly, so avoid a write per tick.
  const zoomSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (zoomSaveTimeoutRef.current) clearTimeout(zoomSaveTimeoutRef.current);
    zoomSaveTimeoutRef.current = setTimeout(() => {
      onZoomChange(ganttZoom);
    }, 600);
    return () => { if (zoomSaveTimeoutRef.current) clearTimeout(zoomSaveTimeoutRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ganttZoom]);

  useEffect(() => {
    const el = ganttScrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setGanttZoom(z => Math.min(10, Math.max(0.2, z * (e.deltaY < 0 ? 1.1 : 1 / 1.1))));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const el = ganttScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const sx = el.scrollLeft;
      el.querySelectorAll<HTMLElement>('.gantt-label-cell').forEach(cell => {
        cell.style.transform = `translateX(${sx}px)`;
      });
      el.querySelectorAll<HTMLElement>('.gantt-bars-cell, .gantt-grid-svg, .gantt-deps-svg').forEach(cell => {
        cell.style.clipPath = `inset(0 0 0 ${sx}px)`;
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = ganttScrollRef.current;
    if (!el) return;
    const sx = el.scrollLeft;
    if (sx === 0) return;
    el.querySelectorAll<HTMLElement>('.gantt-label-cell').forEach(cell => {
      cell.style.transform = `translateX(${sx}px)`;
    });
    el.querySelectorAll<HTMLElement>('.gantt-bars-cell, .gantt-grid-svg, .gantt-deps-svg').forEach(cell => {
      cell.style.clipPath = `inset(0 0 0 ${sx}px)`;
    });
  });

  // ── Export ────────────────────────────────────────────────────────────────

  const exportGanttAs = (format: 'svg' | 'png') => {
    // ganttSortedTasksRef.current is already in displayOrder (see its own comment,
    // near sortedTasks) — i.e. exactly what's currently on screen, override included —
    // so the export is always a faithful export of the current view, never a surprise.
    const result = buildGanttSVGString({
      ganttTasks,
      sortedTaskIds: ganttSortedTasksRef.current.map(t => t.id),
      durationUnit,
      visibleLevels,
      ganttNonWorkingDays,
      ganttWorkDays: workDays,
      ganttStartDate,
      fullDaySchedule,
      dayStartHour,
      hoursPerDay,
      hideNonWorkingHours,
      is24hRealStarts,
      workHoursOnly,
      showAllGanttDeps,
      arrows,
      ganttZoom,
      labelWidth: ganttLabelWidth,
      // Pass the already-computed on-screen pixel-per-column value directly so the export
      // matches the live view exactly, instead of re-deriving zoom from a different denominator.
      screenDw: dw,
      // Crop the export to the "set timeline dates" range, same as the live view —
      // effectiveTimelineView (not the raw shared timelineView) so a preset's own
      // saved range wins while it's active, see its own comment above.
      timelineView: effectiveTimelineView,
      onlyVisibleBars: exportOnlyVisibleBars,
      ganttDurationDisplay,
      // "Filtered Export": explicit per-row pick, independent of onlyVisibleBars above.
      selectedBoxIds: filteredExportEnabled ? exportSelectedBoxIds : null,
      // Mirror whatever the live view currently shows for subitems/Area-toplevel
      // rows — these used to never appear in the export at all.
      subitemGanttTasks,
      boxes,
      areas,
      includeSubitemsInExport: showSubitemsInGantt,
      includeAreaGroupsInExport: showAreaGroupsInGantt,
      includeRegularBoxesInExport: showRegularBoxesInGantt,
      // "Filter by category" was never mirrored into the export before — a
      // filtered-out task still showed up underneath whatever was actually visible.
      categoryFilter,
      departments,
      // "Show start/end column" — same auto-mirror convention as the three
      // includeXInExport flags above.
      startEndText,
      includeStartEndColumnInExport: showStartEndColumnInGantt,
    });
    if (!result) return;
    const { svg, W, H } = result;
    const filename = (title || 'gantt') + '-gantt';
    if (format === 'svg') {
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${filename}.svg`; a.click();
      URL.revokeObjectURL(url);
    } else {
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const img = new window.Image();
      img.onload = () => {
        const scale = 2;
        const canvas = document.createElement('canvas');
        canvas.width = W * scale; canvas.height = H * scale;
        const ctx = canvas.getContext('2d')!;
        ctx.scale(scale, scale); ctx.drawImage(img, 0, 0, W, H);
        URL.revokeObjectURL(url);
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png'); a.download = `${filename}.png`; a.click();
      };
      img.src = url;
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────

  const allCalcTasks = ganttTasks;
  // All real tasks (excl. callouts/startday), independent of the resource filter — critical
  // path is a project-wide property and must be computed over the whole dependency graph,
  // not just the currently-displayed resource subset (a filtered-out predecessor/successor
  // would otherwise look disconnected and break the chain).
  const allTasks = allCalcTasks.filter(t => t.type !== 'callout' && t.type !== 'startday');
  // Resource-filtered view of the same tasks — drives which rows are actually rendered.
  const tasks = allTasks
    .filter(t => resourceFilter.length === 0 || (t.assignee || '').split(',').map(n => n.trim()).some(n => resourceFilter.includes(n)))
    .filter(t => categoryFilter.length === 0 || categoryFilter.includes(departments.find(d => d.color === t.departmentColor)?.name || ''));
  const calcTaskMap: Record<number, typeof allCalcTasks[0]> = {};
  allCalcTasks.forEach(t => { calcTaskMap[t.id] = t; });
  // Subitem rows' own raw (un-adjusted) duration/is24h flags — needed by the is24h
  // real-start lookups below (e.g. the dispStartOverride block), which key off this
  // map and otherwise only ever saw top-level boxes.
  subitemGanttTasks.forEach(t => { calcTaskMap[t.id] = t as any; });

  // Group resources by team for the filter popup; no header if there's only 1 group.
  const resourceGroups: [string, Resource[]][] = (() => {
    const groups = new Map<string, Resource[]>();
    resources.forEach(r => {
      const key = r.team || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    });
    return Array.from(groups.entries());
  })();

  // Sort tasks by ganttOrder; new tasks not yet in order go to the end
  // While "Filtered Export" is on and a saved preset's order is active (exportOrderOverride),
  // show the Gantt itself in that order too — otherwise what's on screen and what gets
  // exported look unrelated, which is confusing (reported directly by a user). This never
  // writes to the shared `ganttOrder` itself — it's a per-session, display-only reordering
  // that reverts to the normal shared order the instant the mode is turned off, so applying
  // a preset can never silently reorder the Gantt for other collaborators viewing it live.
  const displayOrder = (filteredExportEnabled && exportOrderOverride)
    ? [...exportOrderOverride, ...ganttOrder.filter(id => !exportOrderOverride!.includes(id))]
    : ganttOrder;

  const sortedTasks = displayOrder.length > 0
    ? [...tasks].sort((a, b) => {
        const ai = displayOrder.indexOf(a.id);
        const bi = displayOrder.indexOf(b.id);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      })
    : tasks;
  ganttSortedTasksRef.current = sortedTasks;

  // ── Empty state ───────────────────────────────────────────────────────────

  if (allTasks.length === 0) {
    return (
      <div className="w-full bg-blue-50 flex flex-col" style={{ height: '100dvh' }}>
        <div className="bg-white shadow" style={{ overflowX: 'auto', flexShrink: 0 }}>
          <div className="px-4 py-3 flex items-center justify-between gap-3" style={{ minWidth: 'max-content' }}>
            <h1 className="text-xl font-bold text-gray-800">Gantt Chart: {title}</h1>
            <button onClick={onClose} className="bg-blue-500 text-white px-3 py-1 rounded text-sm">
              Back to FlowPlanner
            </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg font-semibold mb-2">No tasks to display</p>
            <p className="text-sm">Add some boxes to your flowchart to see the Gantt chart</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Calendar / column calculations ────────────────────────────────────────

  const toNoon = (d: Date) => { const c = new Date(d); c.setHours(12, 0, 0, 0); return c; };
  const parseD = (s: string) => { const [y,m,d] = s.split('T')[0].split('-').map(Number); return new Date(y, m-1, d, 12, 0, 0); };
  const ganttNoon = toNoon(ganttStartDate);
  const scheduledNoons = tasks.filter(t => t.scheduledStart).map(t => parseD(t.scheduledStart!));
  const originNoon = scheduledNoons.length > 0
    ? new Date(Math.min(ganttNoon.getTime(), ...scheduledNoons.map(d => d.getTime())))
    : ganttNoon;
  const calendarOrigin = new Date(originNoon); calendarOrigin.setHours(0, 0, 0, 0);

  const toDs = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  // `filterWorkDays` controls whether non-work days are excluded from the list
  // `fromDate` overrides calendarOrigin as the list start (for viewport extension)
  const buildColList = (count: number, filterWorkDays: boolean, fromDate?: Date): Date[] => {
    const list: Date[] = [];
    const base = new Date(fromDate ?? calendarOrigin);
    const nwSet = new Set(ganttNonWorkingDays);
    let i = 0;
    while (list.length < count) {
      const d = new Date(base);
      d.setDate(base.getDate() + i); i++;
      const dow = d.getDay();
      if (filterWorkDays && !workDays.includes(dow)) continue;
      if (filterWorkDays && nwSet.has(toDs(d))) continue;
      list.push(d);
    }
    return list;
  };

  // schedWHO: derived from workDays, used for ALL scheduling logic (never the display toggle)
  const schedWHO = workDays.length < 7;
  const colListSize = durationUnit === 'weeks' ? 208 : 730;
  // colList = scheduling column list (always from calendarOrigin — must not change)
  const colList = buildColList(colListSize, schedWHO);

  // While "Filtered Export" is on and a saved preset's timeline range is active
  // (exportTimelineOverride), use THAT range for the viewport crop instead of the
  // shared timelineView — same mode-scoped-override reasoning as displayOrder
  // above (see exportTimelineOverride's own comment). Reverts automatically the
  // instant the mode is turned off.
  const effectiveTimelineView = (filteredExportEnabled && exportTimelineOverride) ? exportTimelineOverride : timelineView;

  // displayOrigin: extend leftward if effectiveTimelineView.start is before calendarOrigin
  const displayOrigin = (() => {
    if (!effectiveTimelineView?.start) return calendarOrigin;
    const d = new Date(effectiveTimelineView.start + 'T00:00:00');
    return d < calendarOrigin ? d : calendarOrigin;
  })();

  // displayColList = for rendering; starts from displayOrigin (may be before calendarOrigin)
  const displayColList = buildColList(colListSize * 2, workHoursOnly ? schedWHO : false, displayOrigin);
  const displayColMap = new Map<string, number>(displayColList.map((d, i) => [toDs(d), i]));

  // Display-only: when "hide non-working hours" is off (hours mode), each day gets a
  // full 24-column slot instead of just the configured Workday's hoursPerDay columns,
  // so 24/5 tasks (which run through the night) can be positioned at their true real
  // clock time instead of being confined to the same work-hour-only grid as regular tasks.
  const showAllHours = durationUnit === 'hours' && !hideNonWorkingHours;
  const displayHoursPerDay = showAllHours ? 24 : hoursPerDay;

  // Convert scheduling-column position to display-column position
  const toDisplayPos = (schedCol: number): number => {
    if (durationUnit === 'hours') {
      const wdi = Math.min(Math.floor(schedCol / hoursPerDay), colList.length - 1);
      const hourInDay = schedCol % hoursPerDay;
      const date = colList[wdi];
      const dispIdx = displayColMap.get(toDs(date)) ?? wdi;
      // showAllHours: each day is 24 columns wide, and the work-hour window sits at
      // [dayStartHour, dayStartHour+hoursPerDay) within it instead of at [0, hoursPerDay).
      return showAllHours ? dispIdx * 24 + dayStartHour + hourInDay : dispIdx * hoursPerDay + hourInDay;
    }
    const di = Math.min(Math.floor(schedCol), colList.length - 1);
    const frac = schedCol - di;
    const date = colList[di];
    const dispIdx = displayColMap.get(toDs(date)) ?? di;
    return dispIdx + frac;
  };

  // Like toDisplayPos but for bar END positions: when non-working days are visible
  // and the end scheduling col lands exactly at the start of a working day that
  // follows non-working days in the display, snap back to just after the previous
  // working day so bars don't visually bleed through weekends.
  const toDisplayEndPos = (schedCol: number): number => {
    if (workHoursOnly || !schedWHO) return toDisplayPos(schedCol);
    if (durationUnit === 'hours') {
      const wdi = Math.min(Math.floor(schedCol / hoursPerDay), colList.length - 1);
      const hourInDay = schedCol % hoursPerDay;
      if (hourInDay === 0 && wdi > 0) {
        const prevDispIdx = displayColMap.get(toDs(colList[wdi - 1])) ?? (wdi - 1);
        const curDispIdx  = displayColMap.get(toDs(colList[wdi]))     ?? wdi;
        if (curDispIdx > prevDispIdx + 1) {
          return showAllHours ? prevDispIdx * 24 + dayStartHour + hoursPerDay : prevDispIdx * hoursPerDay + hoursPerDay;
        }
      }
      return toDisplayPos(schedCol);
    }
    const di = Math.min(Math.floor(schedCol), colList.length - 1);
    const frac = schedCol - di;
    if (frac === 0 && di > 0) {
      const prevDispIdx = displayColMap.get(toDs(colList[di - 1])) ?? (di - 1);
      const curDispIdx  = displayColMap.get(toDs(colList[di]))     ?? di;
      if (curDispIdx > prevDispIdx + 1) return prevDispIdx + 1;
    }
    return toDisplayPos(schedCol);
  };

  // Whether a given display-column index falls on a non-work day, or (when showAllHours
  // is on) a non-working hour of an otherwise working day.
  const isNonWorkDisplayCol = (displayCol: number): boolean => {
    if (showAllHours) {
      const hourInDay = displayCol % 24;
      if (hourInDay < dayStartHour || hourInDay >= dayStartHour + hoursPerDay) return true;
    }
    if (workHoursOnly) return false;
    const dayIdx = durationUnit === 'hours' ? Math.floor(displayCol / displayHoursPerDay) : displayCol;
    const d = displayColList[Math.min(dayIdx, displayColList.length - 1)];
    if (!d) return false;
    const dow = d.getDay();
    return !workDays.includes(dow) || ganttNonWorkingDays.includes(toDs(d));
  };

  const dayIndex = (dateNoon: Date): number => {
    const ty = dateNoon.getFullYear(), tm = dateNoon.getMonth(), td = dateNoon.getDate();
    for (let i = 0; i < colList.length; i++) {
      const d = colList[i];
      if (d.getFullYear() === ty && d.getMonth() === tm && d.getDate() === td) return i;
      const dn = new Date(d); dn.setHours(12, 0, 0, 0);
      if (dn > dateNoon) return i;
    }
    return colList.length - 1;
  };

  // Real-clock Date -> display column, for showAllHours mode. Regular tasks stay
  // positioned via toDisplayPos (their schedule column already implies a real hour
  // within the work window), but an is24h task's schedule column doesn't linearly
  // correspond to real time once chained (see getEndCol's comment) — its bar must be
  // placed from its actual real start/end instead, found here directly in display space.
  const realDateToDisplayCol = (dt: Date): number => {
    const dayNoon = new Date(dt); dayNoon.setHours(12, 0, 0, 0);
    const ty = dayNoon.getFullYear(), tm = dayNoon.getMonth(), td = dayNoon.getDate();
    let di = displayColList.length - 1;
    for (let i = 0; i < displayColList.length; i++) {
      const d = displayColList[i];
      if (d.getFullYear() === ty && d.getMonth() === tm && d.getDate() === td) { di = i; break; }
      if (d > dayNoon) { di = i; break; }
    }
    return di * 24 + dt.getHours() + dt.getMinutes() / 60;
  };

  const dateToCol = (dateNoon: Date): number => {
    if (durationUnit === 'weeks') {
      return Math.round((dateNoon.getTime() - originNoon.getTime()) / (7 * 86400000));
    }
    const idx = dayIndex(dateNoon);
    return durationUnit === 'hours' ? idx * hoursPerDay : idx;
  };

  const originShift = dateToCol(ganttNoon);

  const getCalDays = (durStr: string | undefined): number => {
    if (!durStr) return 1;
    const m = durStr.match(/^(\d+(?:\.\d+)?)\s*([hdwHDW]?)$/);
    if (!m) return 1;
    const n = parseFloat(m[1]);
    if (n === 0) return 0;
    const u = (m[2] || 'd').toLowerCase();
    return u === 'w' ? n * 7 : u === 'h' ? Math.max(1, Math.ceil(n / 24)) : n;
  };

  // Working-day count for 24/5 duration strings (weeks → ×5, hours → ÷24, days → as-is)
  const workDaysCount2 = (durStr: string | undefined): number => {
    if (!durStr) return 1;
    const m2 = durStr.match(/^(\d+(?:\.\d+)?)\s*([hdwHDW]?)$/);
    if (!m2) return 1;
    const n2 = parseFloat(m2[1]);
    if (n2 === 0) return 0;
    const u2 = (m2[2] || 'd').toLowerCase();
    return u2 === 'w' ? Math.round(n2 * 5) : u2 === 'h' ? Math.max(1, Math.ceil(n2 / 24)) : Math.round(n2);
  };

  // Advance startD by n working days (respects workDays + ganttNonWorkingDays)
  const advanceWorkDays = (startD: Date, n: number): Date => {
    const d = new Date(startD);
    const nwSetWD = new Set(ganttNonWorkingDays);
    const toDs2 = (dt: Date) =>
      `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      if (workDays.includes(d.getDay()) && !nwSetWD.has(toDs2(d))) added++;
    }
    return d;
  };

  const calloutBoxIds2 = new Set(boxes.filter(b => b.type === 'callout').map(b => b.id));
  const adjDeps: Record<number, number[]> = {};
  arrows.forEach(a => {
    if (calloutBoxIds2.has(a.fromBox) || calloutBoxIds2.has(a.toBox)) return;
    if (!adjDeps[a.toBox]) adjDeps[a.toBox] = [];
    adjDeps[a.toBox].push(a.fromBox);
  });

  // Real (unsnapped) clock end of an is24h task's execution, given its real clock
  // start (from is24hRealStarts, which walks the whole is24h ancestor chain in
  // real time — see getAlwaysOnClockStart in FlowchartBuilder.tsx).
  const is24hRealEnd = (startClock: Date, task: { is24_5?: boolean; duration?: number }): Date =>
    is24hEndFromStart(startClock, task.duration ?? 0, task.is24_5 ?? false, ganttNonWorkingDays, workDays);

  // Converts a real clock Date into the shared 9am-5pm/8h-per-day column grid used
  // for hours-mode positioning, snapping forward to the next work morning when it
  // falls outside business hours. dayIndex already counts from calendarOrigin (the
  // same origin getAdjStart's own values are relative to), so no originShift
  // adjustment is needed here.
  const dateToHourGridCol = (dt: Date): number => {
    const hpdH = hoursPerDay; const dayStartH = dayStartHour;
    const eh = dt.getHours() + dt.getMinutes() / 60;
    if (eh >= dayStartH + hpdH || eh <= dayStartH) {
      const next = new Date(dt);
      if (eh >= dayStartH + hpdH) next.setDate(next.getDate() + 1);
      next.setHours(12, 0, 0, 0);
      const nwSet = new Set(ganttNonWorkingDays);
      while (!workDays.includes(next.getDay()) || nwSet.has(toDs(next))) next.setDate(next.getDate() + 1);
      return dayIndex(next) * hpdH;
    }
    const dayNoon = new Date(dt); dayNoon.setHours(12, 0, 0, 0);
    return dayIndex(dayNoon) * hpdH + Math.max(0, eh - dayStartH);
  };

  const adjStarts: Record<number, number> = {};
  const adjVisit = new Set<number>();

  const getEndCol = (predId: number): number => {
    const pred = calcTaskMap[predId];
    if (!pred) return getAdjStart(predId) + 1;
    if (pred.type === 'startday') return parseInt(pred.text) || 0;
    const s = getAdjStart(predId);
    if ((pred.alwaysOn || pred.is24_5 || fullDaySchedule) && schedWHO && durationUnit === 'days' && s < colList.length) {
      const startD = new Date(colList[s]); startD.setHours(12, 0, 0, 0);
      const boxDur = boxes.find(b => b.id === predId)?.duration;
      const endD = (pred.is24_5 && !pred.alwaysOn && !fullDaySchedule)
        ? advanceWorkDays(startD, workDaysCount2(boxDur))
        : (() => { const d = new Date(startD); d.setDate(d.getDate() + getCalDays(boxDur)); return d; })();
      return dateToCol(endD);
    }
    if ((pred.alwaysOn || pred.is24_5) && !fullDaySchedule && durationUnit === 'hours') {
      // The predecessor's real clock start must come from is24hRealStarts, which
      // walks the whole is24h ancestor chain in real time (same logic as
      // getAlwaysOnClockStart in FlowchartBuilder.tsx, used for the box's own
      // displayed start/end label). Decoding it from s via endOffsetToDate instead
      // is only valid for a lone is24h task anchored directly to the project start:
      // s lives in a column space where "8 units" means one calendar day for an
      // is24h chain (24 real hours compressed 3:1) but endOffsetToDate always
      // decodes "8 units" as an 8-hour, 9am-5pm workday — so any predecessor whose
      // own start was itself derived from an earlier is24h hop (a chain of two or
      // more 24/5 tasks) decodes to the wrong clock time, and that error compounds
      // with every additional hop.
      const startClock = is24hRealStarts.get(predId) ?? endOffsetToDate(s, true);
      const endClock = is24hRealEnd(startClock, pred);
      // No rounding: endClock's hour-of-day can be non-whole (e.g. a 24/7 predecessor
      // finishing at 14:30) — dateToHourGridCol preserves the fraction so this
      // successor's start stays in sync with the predecessor's own displayed end
      // time, which uses exact clock arithmetic (getEnd/getAlwaysOnClockStart).
      return dateToHourGridCol(endClock);
    }
    return s + (pred.duration ?? 1);
  };

  const getAdjStart = (id: number): number => {
    if (adjStarts[id] !== undefined) return adjStarts[id];
    if (adjVisit.has(id)) return 0;
    adjVisit.add(id);

    const t = calcTaskMap[id];
    let result: number;
    if (!t) {
      result = originShift;
    } else if (t.scheduledStart) {
      // dateToCol(parseD(...)) drops the pin's time-of-day (parseD parses date-only,
      // at noon), landing every pinned task at the start of its work window instead of
      // its actual pinned time. For an is24h task, use the real clock start from
      // is24hRealStarts (needed since its column space doesn't decode linearly once
      // chained — though a pinned task's own start is never chain-derived, this stays
      // consistent with every other is24h real-time lookup). For a regular task, build
      // the pinned Date directly and run it through the same grid conversion — mirrors
      // calcStart's own scheduledStart hourOff formula in lib/gantt.ts.
      const hasTime = t.scheduledStart.includes('T');
      const is24h = (t.alwaysOn || t.is24_5) && !fullDaySchedule && durationUnit === 'hours';
      let realStart: Date | undefined;
      if (hasTime && durationUnit === 'hours' && !fullDaySchedule) {
        if (is24h) {
          realStart = is24hRealStarts.get(id);
        } else {
          const [datePart, timePart] = t.scheduledStart.split('T');
          const [py, pm, pd] = datePart.split('-').map(Number);
          const [ph, pmi] = timePart.split(':').map(Number);
          realStart = new Date(py, pm - 1, pd, ph || 0, pmi || 0, 0);
        }
      }
      result = realStart ? dateToHourGridCol(realStart) : dateToCol(parseD(t.scheduledStart));
    } else if (t.type === 'startday') {
      result = parseInt(t.text) || 0;
    } else {
      const preds = adjDeps[id] || [];
      let mx = preds.length > 0
        ? Math.max(...preds.map(predId => {
            const pred = calcTaskMap[predId];
            if (pred?.type === 'startday') return parseInt(pred.text) || 0;
            return getEndCol(predId);
          }))
        : originShift;
      if (t.earliestStart && !t.earliestStart.startsWith('W')) {
        mx = Math.max(mx, dateToCol(parseD(t.earliestStart)));
      } else if (t.earliestStart && t.earliestStart.startsWith('W')) {
        mx = Math.max(mx, calcEarliestStartOffset(t) + originShift);
      }
      result = mx;
    }
    adjStarts[id] = result;
    adjVisit.delete(id);
    return result;
  };
  // Extracted so the resource-summary rows below (which need EVERY task project-wide,
  // not just the resource/category-filtered `tasks`) can convert into the same
  // calendar-origin-relative coordinate space as everything else here, instead of
  // feeding toDisplayPos/toDisplayEndPos the raw, ganttStartDate-relative start/
  // duration straight off `allTasks` — see getResourceSummaryRanges's own comment
  // for the crash that mismatch caused.
  const adjustTask = (t: typeof allTasks[number]) => {
    const s = getAdjStart(t.id);
    let dur = t.duration;
    if ((t.alwaysOn || t.is24_5 || fullDaySchedule) && schedWHO && durationUnit === 'days' && s < colList.length) {
      const startD = new Date(colList[s]); startD.setHours(12, 0, 0, 0);
      const boxDur = boxes.find(b => b.id === t.id)?.duration;
      const endD = (t.is24_5 && !t.alwaysOn && !fullDaySchedule)
        ? advanceWorkDays(startD, workDaysCount2(boxDur))
        : (() => { const d = new Date(startD); d.setDate(d.getDate() + getCalDays(boxDur)); return d; })();
      dur = Math.max(0.5, dateToCol(endD) - s);
    } else if ((t.alwaysOn || t.is24_5) && !fullDaySchedule && durationUnit === 'hours' && t.duration > 0) {
      // In hours mode, alwaysOn/is24_5 durations are in clock hours but scheduling
      // columns are working hours (8h/col). The task's real clock start comes from
      // is24hRealStarts (walks the whole is24h ancestor chain in real time — see
      // getEndCol above for why decoding it from s instead would be wrong for a
      // chained predecessor), and the resulting end is converted back to a column
      // for correct bar width.
      const startClock = is24hRealStarts.get(t.id) ?? endOffsetToDate(s, true);
      const endClock = is24hRealEnd(startClock, t as any);
      const endCol = dateToHourGridCol(endClock);
      dur = Math.max(0.5, endCol - s);
    }
    return { ...t, start: s, duration: dur };
  };
  tasks.forEach(t => getAdjStart(t.id));
  const adjustedTasks = tasks.map(adjustTask);
  // Project-wide (unfiltered) counterpart of adjustedTasks — see adjustTask's own comment.
  allTasks.forEach(t => getAdjStart(t.id));
  const allTasksAdjusted = allTasks.map(adjustTask);

  // Same displayOrder (see sortedTasks' own comment) as the plain, non-calendar-adjusted
  // sort above — kept as a separate sort because this one sorts adjustedTasks, not tasks.
  const sortedTasksAdjusted = displayOrder.length > 0
    ? [...adjustedTasks].sort((a, b) => {
        const ai = displayOrder.indexOf(a.id); const bi = displayOrder.indexOf(b.id);
        if (ai === -1 && bi === -1) return 0; if (ai === -1) return 1; if (bi === -1) return -1;
        return ai - bi;
      })
    : adjustedTasks;

  // ── Critical path ─────────────────────────────────────────────────────────
  // Pre-computed by FlowchartBuilder (lib/gantt's calcCriticalPath) over the FULL,
  // unfiltered task graph — critical path is a project-wide property, so it must not be
  // affected by this view's resource filter (which only narrows which rows are displayed).
  const criticalPathIds = showCriticalPath ? criticalPath.boxIds : new Set<number>();
  const criticalPathArrowIds = showCriticalPath ? criticalPath.arrowIds : new Set<number>();

  // ── Drag to reorder ───────────────────────────────────────────────────────

  const startGanttDrag = (e: React.MouseEvent, fromIdx: number) => {
    e.preventDefault();
    e.stopPropagation();
    ganttDragFromRef.current = fromIdx;
    setGanttDragIdx(fromIdx);
    ganttDropIdxRef.current = fromIdx;
    setGanttDropIdx(fromIdx);

    // Auto-scroll ganttScrollRef (the panel's own vertical+horizontal scroll
    // container) while the drag is held near its top/bottom edge — without this, a
    // long task list's rows above/below whatever's currently scrolled into view were
    // simply unreachable as a drop target, same underlying issue (and same fix) as
    // MindmapBuilder.tsx's priority-matrix card drag.
    const GANTT_AUTOSCROLL_EDGE = 36;
    const GANTT_AUTOSCROLL_SPEED = 10;
    let autoScrollDir: 1 | -1 | null = null;
    let lastClientY = e.clientY;

    // Shared by the real mousemove handler and the scroll-tick interval below (the
    // interval re-runs this against the last known pointer position so the drop
    // target keeps tracking the content scrolling underneath a pointer that's
    // itself stayed still, held at the edge).
    const updateDropIndexAndAutoScroll = (clientY: number) => {
      lastClientY = clientY;
      const container = ganttTasksContainerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        const relY = clientY - rect.top;
        // relY/48 is a position in the RENDERED row list — which includes interleaved
        // subitem/Area-header rows whenever those are shown, making it longer than the
        // top-level-only list onUp actually reorders. Translate it back: find the
        // largest top-level index whose own expanded-space position is still at or
        // before this pixel row, i.e. "which top-level slot does this row fall in or
        // just after". Without this, dropping at a screen position was silently
        // reordering a completely different (often many rows off) top-level task —
        // reported as the drag handle "thinking the bar is elsewhere".
        const rawIdx = Math.round(relY / 48);
        const map = expandedIndexForTopLevelIndexRef.current;
        let idx = rawIdx;
        if (map.length > 0) {
          idx = 0;
          for (let i = 0; i < map.length; i++) {
            if (map[i] !== undefined && map[i] <= rawIdx) idx = i;
          }
        }
        const clamped = Math.max(0, Math.min(ganttSortedTasksRef.current.length, idx));
        setGanttDropIdx(clamped);
        ganttDropIdxRef.current = clamped;
      }

      const scrollEl = ganttScrollRef.current;
      if (scrollEl) {
        const sRect = scrollEl.getBoundingClientRect();
        if (clientY < sRect.top + GANTT_AUTOSCROLL_EDGE && scrollEl.scrollTop > 0) {
          autoScrollDir = -1;
        } else if (clientY > sRect.bottom - GANTT_AUTOSCROLL_EDGE && scrollEl.scrollTop + scrollEl.clientHeight < scrollEl.scrollHeight - 1) {
          autoScrollDir = 1;
        } else {
          autoScrollDir = null;
        }
      }
    };

    const onMove = (ev: MouseEvent) => updateDropIndexAndAutoScroll(ev.clientY);

    const scrollTickId = setInterval(() => {
      if (autoScrollDir === null) return;
      const scrollEl = ganttScrollRef.current;
      if (!scrollEl) return;
      scrollEl.scrollTop += autoScrollDir * GANTT_AUTOSCROLL_SPEED;
      updateDropIndexAndAutoScroll(lastClientY);
    }, 16);

    const onUp = () => {
      const from = ganttDragFromRef.current;
      const toRaw = ganttDropIdxRef.current;
      if (from !== null && toRaw !== null) {
        const adj = toRaw > from ? toRaw - 1 : toRaw;
        if (adj !== from) {
          const ts = [...ganttSortedTasksRef.current];
          const [item] = ts.splice(from, 1);
          ts.splice(adj, 0, item);
          onGanttOrderChange(ts.map(t => t.id));
          // A manual drag is an explicit reorder action — let it win outright and
          // exit preset-preview mode, rather than have exportOrderOverride silently
          // reassert itself over what the user just dragged.
          setExportOrderOverride(null);
        }
      }
      ganttDragFromRef.current = null;
      setGanttDragIdx(null);
      setGanttDropIdx(null);
      ganttDropIdxRef.current = null;
      clearInterval(scrollTickId);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Drag-to-reorder for "one summary row per resource" rows. Reorders resourceFilter
  // itself (not a separate order array) — that's already the thing that determines
  // section order in the stacked multi-resource view too, and it's already persisted
  // to localStorage per flowchart via onResourceFilterChange, so reordering it here
  // gets that persistence for free.
  const startResourceSummaryDrag = (e: React.MouseEvent, fromIdx: number) => {
    e.preventDefault();
    e.stopPropagation();
    resourceDragFromRef.current = fromIdx;
    setResourceDragIdx(fromIdx);
    resourceDropIdxRef.current = fromIdx;
    setResourceDropIdx(fromIdx);

    const onMove = (ev: MouseEvent) => {
      const container = resourceRowsContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const relY = ev.clientY - rect.top;
      const idx = Math.round(relY / 48);
      const clamped = Math.max(0, Math.min(resourceFilter.length, idx));
      setResourceDropIdx(clamped);
      resourceDropIdxRef.current = clamped;
    };

    const onUp = () => {
      const from = resourceDragFromRef.current;
      const toRaw = resourceDropIdxRef.current;
      if (from !== null && toRaw !== null) {
        const adj = toRaw > from ? toRaw - 1 : toRaw;
        if (adj !== from) {
          const next = [...resourceFilter];
          const [item] = next.splice(from, 1);
          next.splice(adj, 0, item);
          setResourceFilter(next);
        }
      }
      resourceDragFromRef.current = null;
      setResourceDragIdx(null);
      setResourceDropIdx(null);
      resourceDropIdxRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ── Calendar headers ──────────────────────────────────────────────────────

  const generateCalendarHeaders = (maxDuration: number, unit: string, startDate: Date, workHoursOnlyMode: boolean, nonWorkingDays: string[] = [], fullDayMode = false, activeWorkDays: number[] = [1,2,3,4,5]) => {
    const nonWorkingSet = new Set(nonWorkingDays);
    const toDateStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const date = new Date(startDate);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const hPerDay = fullDayMode ? 24 : displayHoursPerDay;

    const unitsPerWorkDay = unit === 'hours' ? hPerDay : unit === 'days' ? 1 : 0.2;
    const unitsPerHour    = unit === 'hours' ? 1 : unit === 'days' ? 0.125 : 0.025;

    const neededWorkDays = unit === 'hours' ? Math.ceil(maxDuration / hPerDay)
      : unit === 'days' ? maxDuration
      : Math.ceil(maxDuration * 5);

    const workDayList: { date: Date; dow: number; isWeekday: boolean; isNonWorking: boolean }[] = [];
    let calDay = 0;
    while (workDayList.length < neededWorkDays) {
      const cur = new Date(date);
      cur.setDate(date.getDate() + calDay++);
      const dow = cur.getDay();
      const isWeekday = activeWorkDays.includes(dow);
      if (workHoursOnlyMode && !isWeekday) continue;
      const isNonWorking = nonWorkingSet.has(toDateStr(cur));
      if (workHoursOnlyMode && isNonWorking) continue;
      workDayList.push({ date: cur, dow, isWeekday, isNonWorking });
    }

    const getCalInfo = (dt: Date) => {
      const m = monthNames[dt.getMonth()];
      const q = `${dt.getFullYear().toString().slice(-2)}Q${Math.floor(dt.getMonth() / 3) + 1}`;
      const soy = new Date(dt.getFullYear(), 0, 1);
      const pastDays = (dt.getTime() - soy.getTime()) / 86400000;
      const wNum = `W${Math.ceil((pastDays + soy.getDay() + 1) / 7)}`;
      return { m, q, wNum };
    };

    const quarters: { label: string; span: number }[] = [];
    const months:   { label: string; span: number }[] = [];
    const weeks:    { label: string; span: number }[] = [];
    const days:     { day: number; dayName: string; isWeekday: boolean; isNonWorking: boolean; span: number }[] = [];
    const hours:    { label: string; span: number }[] = [];

    let qLabel: string | null = null, qSpan = 0;
    let mLabel: string | null = null, mSpan = 0;
    let wLabel: string | null = null, wSpan = 0;

    for (const wd of workDayList) {
      const { m, q, wNum } = getCalInfo(wd.date);
      const ds = unitsPerWorkDay;

      if (q !== qLabel) { if (qLabel) quarters.push({ label: qLabel, span: qSpan }); qLabel = q; qSpan = ds; } else qSpan += ds;
      if (m !== mLabel) { if (mLabel) months.push({ label: mLabel, span: mSpan }); mLabel = m; mSpan = ds; } else mSpan += ds;
      if (wNum !== wLabel) { if (wLabel) weeks.push({ label: wLabel, span: wSpan }); wLabel = wNum; wSpan = ds; } else wSpan += ds;

      days.push({ day: wd.date.getDate(), dayName: dayNames[wd.dow], isWeekday: wd.isWeekday, isNonWorking: wd.isNonWorking, span: ds });

      const hStart = (fullDayMode || showAllHours) ? 0 : dayStartHour;
      const hEnd   = (fullDayMode || showAllHours) ? 24 : dayStartHour + hoursPerDay;
      for (let h = hStart; h < hEnd; h++) {
        hours.push({ label: `${h}:00`, span: unitsPerHour });
      }
    }
    if (qLabel) quarters.push({ label: qLabel, span: qSpan });
    if (mLabel) months.push({ label: mLabel, span: mSpan });
    if (wLabel) weeks.push({ label: wLabel, span: wSpan });

    return { quarters, months, weeks, days, hours };
  };

  // ── Final layout values ───────────────────────────────────────────────────

  const maxEndRaw = sortedTasksAdjusted.length > 0 ? Math.max(...sortedTasksAdjusted.map(t => t.start + t.duration)) : 1;
  const maxEnd = Math.ceil(Math.max(maxEndRaw, 1));
  const displayMaxEnd = Math.ceil(Math.max(toDisplayPos(maxEnd), 1));

  // Viewport: optional custom date range (effectiveTimelineView.start / .end — see
  // its own comment above for why this isn't always the same as timelineView)
  const dayColForIso = (iso: string): number => {
    const di = displayColMap.get(iso);
    return di !== undefined ? (durationUnit === 'hours' ? di * displayHoursPerDay : di) : -1;
  };
  const viewStartColIdx = (() => {
    if (!effectiveTimelineView?.start) return 0;
    const c = dayColForIso(effectiveTimelineView.start);
    return c >= 0 ? c : 0;
  })();
  const viewEndColIdx = (() => {
    if (!effectiveTimelineView?.end) return displayMaxEnd;
    const di = displayColMap.get(effectiveTimelineView.end);
    if (di === undefined) return displayMaxEnd;
    const c = durationUnit === 'hours' ? (di + 1) * displayHoursPerDay : di + 1;
    return Math.min(c, displayColList.length * (durationUnit === 'hours' ? displayHoursPerDay : 1));
  })();
  const renderColCount = Math.max(viewEndColIdx - viewStartColIdx, 1);
  // showAllHours triples the column count per day (24 vs hoursPerDay) — scale the pixel
  // budget by the same ratio so each column's width (and visual density per real hour)
  // stays the same as before toggling; only the chart's total width grows to fit the
  // now-visible non-working hours, matching how showing non-working days already works.
  const widthScale = durationUnit === 'hours' ? displayHoursPerDay / hoursPerDay : 1;
  const dw = (800 * ganttZoom * widthScale) / renderColCount;
  const headerOriginDay = durationUnit === 'hours' ? Math.floor(viewStartColIdx / displayHoursPerDay) : viewStartColIdx;
  const headerOrigin = displayColList[Math.min(headerOriginDay, displayColList.length - 1)] ?? calendarOrigin;
  const calendarHeaders = generateCalendarHeaders(renderColCount, durationUnit, headerOrigin, workHoursOnly, ganttNonWorkingDays, fullDaySchedule, workDays);

  // "Hide empty rows" (View menu, exportOnlyVisibleBars under the hood — see its own
  // comment): drop any row whose bar has no overlap with the current timeline view at
  // all, live on screen, using the exact same overlap test lib/svgExport.ts's export
  // path already applies. Only meaningful once a timeline view narrower than the full
  // project range is active (see viewStartColIdx/viewEndColIdx above) — without one,
  // every task is "in view" by definition.
  const ganttRowTasks = (exportOnlyVisibleBars && effectiveTimelineView)
    ? sortedTasksAdjusted.filter(t => {
        const dispStart = toDisplayPos(t.start);
        const dispEnd = t.duration > 0 ? toDisplayEndPos(t.start + t.duration) : dispStart;
        return dispEnd >= viewStartColIdx && dispStart <= viewEndColIdx;
      })
    : sortedTasksAdjusted;

  // "Select specific resource times": subdivides a task's duration into small toggle cells,
  // one per displayed unit (half-hours in hours-mode — finer than a whole hour, since a
  // resource's actual working pattern often splits mid-hour; days otherwise; weeks-mode
  // uses 5 workday-cells per week since a single week is too coarse to subdivide
  // meaningfully). Unit size for one cell, in the same units as `duration` itself.
  const resourceCellUnit = durationUnit === 'weeks' ? 0.2 : durationUnit === 'hours' ? 0.5 : 1;
  // Uses ceil (not round), so every cell is AT MOST one unit wide, with any leftover
  // absorbed by the final cell — e.g. an 11.75h task (not a whole multiple of the 0.5h
  // unit) gets 23 full half-hour cells plus one 0.25h cell, not 24 "evenly sized"
  // ~0.49h cells. The latter (this function's previous behavior, and the reason
  // ceil+remainder exists at all — see git history) made toggling cells near the end
  // of a fractional-duration task remove a different amount of real time than it
  // visually looked like.
  const resourceHoursCellCount = (duration: number): number =>
    Math.max(1, Math.ceil(duration / resourceCellUnit - 1e-9));
  // The real duration-unit offset (clamped to `duration`) where cell `cellIndex`
  // starts/ends — NOT cellIndex * (duration / n), which is only correct when duration
  // happens to be a whole multiple of resourceCellUnit. See resourceHoursCellCount's
  // comment above for why that distinction matters.
  const resourceCellOffset = (duration: number, cellIndex: number): number =>
    Math.min(cellIndex * resourceCellUnit, duration);
  // Converts a [schedStart, schedEnd) sub-span of a REGULAR (non-is24h) task's own
  // schedule-column span — one "Select specific resource times" cell, or one
  // contiguous active/inactive run of them — into one or more [left, right]
  // DISPLAY-COLUMN segments. More than one exactly when the span crosses a workday
  // boundary while non-working hours are visible (durationUnit 'hours' + showAllHours):
  // mirrors the main bar's own shouldSplitHours day-splitting further below. Without
  // this, a cell/run that happens to straddle midnight rendered as ONE huge block
  // spanning the entire overnight gap (e.g. a single cell ballooning from ~1 display
  // column to ~16), instead of stopping at end-of-workday and resuming next work
  // morning like the bar itself already does. `isAtTaskEnd` substitutes the task's own
  // exact end (toDisplayEndPos-based, avoids weekend-bleed) for the final segment's
  // right edge, instead of a possibly-wrong raw toDisplayPos(schedEnd).
  const splitRangeAtNightGaps = (
    schedStart: number,
    schedEnd: number,
    barEndDisp: number,
    isAtTaskEnd: boolean,
  ): Array<[number, number]> => {
    const hoursShowAll = durationUnit === 'hours' && showAllHours;
    const nightGap = 24 - hoursPerDay;
    // A RIGHT edge landing exactly at hour 0 of a new day is ambiguous in showAllHours
    // mode: toDisplayPos places it at the START of that next day's 24-column slot, but
    // as an END (exclusive) boundary it must mean "end of the PREVIOUS day's workday"
    // instead — the while loop below already corrects for this at every boundary it
    // walks past, but a cell/run whose end happens to land EXACTLY on a boundary (not
    // past it) fell through to the un-corrected toDisplayPos(schedEnd) fallback.
    const endPos = (schedCol: number): number =>
      (hoursShowAll && schedCol > 0 && schedCol % hoursPerDay === 0) ? toDisplayPos(schedCol) - nightGap : toDisplayPos(schedCol);
    if (!hoursShowAll || schedEnd <= schedStart) {
      return [[toDisplayPos(schedStart), isAtTaskEnd ? barEndDisp : endPos(schedEnd)]];
    }
    const segs: Array<[number, number]> = [];
    let segStart = schedStart;
    let boundary = Math.floor(segStart / hoursPerDay) * hoursPerDay + hoursPerDay;
    while (boundary < schedEnd) {
      segs.push([toDisplayPos(segStart), endPos(boundary)]);
      segStart = boundary;
      boundary += hoursPerDay;
    }
    segs.push([toDisplayPos(segStart), isAtTaskEnd ? barEndDisp : endPos(schedEnd)]);
    return segs;
  };
  // Builds the renderable [left,right] display-column segment(s) for every "Select
  // specific resource times" cell of a task — more than one segment for a single cell
  // exactly when that cell's real span crosses a workday boundary (see
  // splitRangeAtNightGaps above). Shared by the edit-mode toggle strip, the
  // inactive-hours mask overlay, and the resource-summary-row active-range merge below
  // — all three need the same per-cell (possibly multi-segment) positions, just
  // consumed differently. `is24hReal` real-clock positioning is already continuous
  // (an is24h task never pauses overnight), so it never needs splitting.
  const buildResourceCellSegments = (
    taskStart: number,
    duration: number,
    n: number,
    is24hReal: Date | undefined,
    barEndDisp: number,
  ): Array<{ cellIndex: number; isLastSegOfCell: boolean; left: number; right: number }> => {
    const segs: Array<{ cellIndex: number; isLastSegOfCell: boolean; left: number; right: number }> = [];
    for (let i = 0; i < n; i++) {
      const offStart = resourceCellOffset(duration, i);
      const offEnd = resourceCellOffset(duration, i + 1);
      if (is24hReal) {
        segs.push({
          cellIndex: i,
          isLastSegOfCell: true,
          left: realDateToDisplayCol(new Date(is24hReal.getTime() + offStart * 3600000)),
          right: realDateToDisplayCol(new Date(is24hReal.getTime() + offEnd * 3600000)),
        });
        continue;
      }
      const parts = splitRangeAtNightGaps(taskStart + offStart, taskStart + offEnd, barEndDisp, i === n - 1);
      parts.forEach(([l, r], pi) => segs.push({ cellIndex: i, isLastSegOfCell: pi === parts.length - 1, left: l, right: r }));
    }
    return segs;
  };
  // A task's duration change shifts its cell count without ever touching the stored
  // resourceHours array (see useBoxActions.ts — nothing resizes it on duration edit),
  // so a previously-saved pattern almost always has a different length than the
  // task's CURRENT cell count. Proportionally resample it to the new length instead of
  // discarding it — each new cell samples the old cell its (index+0.5)-fraction of the
  // way through the old array falls into, which both stretches (fewer->more cells) and
  // shrinks (more->fewer, picking one representative old cell per new one) sanely.
  // Nothing here mutates or persists the stored array; it's recomputed fresh on every
  // read, exactly like the "no override at all" fallback it replaces — the resized
  // shape only gets written back to the box if the user then edits a cell (see
  // onResourceHoursChange below, which always saves the array it's currently showing).
  const rescaleResourceCells = (stored: boolean[], newN: number): boolean[] => {
    const oldN = stored.length;
    if (oldN === newN) return stored;
    return Array.from({ length: newN }, (_, i) => stored[Math.min(oldN - 1, Math.floor((i + 0.5) * oldN / newN))]);
  };
  const getResourceActiveCells = (t: { duration: number; resourceHours?: Record<string, boolean[]> }, resource: string): boolean[] => {
    const n = resourceHoursCellCount(t.duration);
    const stored = t.resourceHours?.[resource];
    if (!stored || stored.length === 0) return new Array(n).fill(true); // no override at all — active for the whole duration
    return stored.length === n ? stored : rescaleResourceCells(stored, n);
  };

  // ── Resource summary rows ("collapse to one row per resource") ─────────────
  // Returns this task's active display-column ranges for `resource`, merging
  // contiguous active cells (per "Select specific resource times") into runs.
  // Same per-cell position math as the inactive-hours mask overlay above, just
  // inverted (active instead of inactive) and returning display columns instead
  // of pixels — the summary row rescales to pixels itself, same as any other row.
  const getResourceActiveDisplayRanges = (t: typeof sortedTasksAdjusted[number], resource: string): Array<[number, number]> => {
    const is24hReal = showAllHours && (t.alwaysOn || t.is24_5) ? is24hRealStarts.get(t.id) : undefined;
    const rawTask = is24hReal ? calcTaskMap[t.id] : undefined;
    const duration = is24hReal && rawTask ? (rawTask.duration ?? t.duration) : t.duration;
    const n = resourceHoursCellCount(duration);
    const stored = t.resourceHours?.[resource];
    if (!stored || stored.length === 0) {
      // No override at all — active for the task's entire span. Compute this as ONE
      // range using the same start/end the task's own bar uses, rather than
      // decomposing into n cells and merging them back: for a long task that's up to n
      // adjacent tiny ranges, and any rounding/day-boundary snapping mismatch between
      // consecutive cells' independently-computed positions shows up as a visible gap
      // instead of one continuous bar — exactly the "not shown as continuously busy"
      // symptom this was written to avoid.
      if (is24hReal && rawTask) {
        const realEnd = is24hRealEnd(is24hReal, rawTask as any);
        return [[realDateToDisplayCol(is24hReal), realDateToDisplayCol(realEnd)]];
      }
      const dispStart = toDisplayPos(t.start);
      const dispEnd = duration > 0 ? toDisplayEndPos(t.start + duration) : dispStart;
      return [[dispStart, dispEnd]];
    }
    // A stored override whose length no longer matches n (duration changed since it
    // was saved) is proportionally resampled instead of discarded — see
    // rescaleResourceCells's own comment above.
    const cells = stored.length === n ? stored : rescaleResourceCells(stored, n);
    const barEndDisp = duration > 0 ? toDisplayEndPos(t.start + duration) : toDisplayPos(t.start + duration);
    const segs = buildResourceCellSegments(t.start, duration, n, is24hReal && rawTask ? is24hReal : undefined, barEndDisp);
    // Merge consecutive ACTIVE segments into runs for display — but only when a segment
    // picks up exactly where the previous cell's own last segment leaves off. Two
    // segments of the SAME cell (split by splitRangeAtNightGaps above, i.e. this one
    // cell's own span crosses a workday boundary) must NOT merge into one continuous
    // range — that's exactly the night-gap-swallowing bug this all exists to avoid.
    const ranges: Array<[number, number]> = [];
    let curLeft: number | null = null;
    let curRight: number | null = null;
    for (let k = 0; k < segs.length; k++) {
      const seg = segs[k];
      if (!cells[seg.cellIndex]) {
        if (curLeft !== null) { ranges.push([curLeft, curRight!]); curLeft = null; curRight = null; }
        continue;
      }
      if (curLeft === null) { curLeft = seg.left; curRight = seg.right; continue; }
      const prevSeg = segs[k - 1];
      const continuesPrevCell = prevSeg.isLastSegOfCell && prevSeg.cellIndex === seg.cellIndex - 1;
      if (continuesPrevCell) curRight = seg.right;
      else { ranges.push([curLeft, curRight!]); curLeft = seg.left; curRight = seg.right; }
    }
    if (curLeft !== null) ranges.push([curLeft, curRight!]);
    return ranges;
  };

  // Union of active ranges across every task assigned to `resource` (not just the
  // currently rendered section's tasks — a resource's summary must reflect ALL its
  // work, regardless of which other resources are also checked in the filter).
  // Must use allTasksAdjusted (calendar-origin-relative), not raw allTasks
  // (ganttStartDate-relative) — toDisplayPos/toDisplayEndPos inside
  // getResourceActiveDisplayRanges expect the former. Any task pinned (scheduledStart)
  // before ganttStartDate has a NEGATIVE raw start, which fed into colList[idx] as a
  // negative index returned undefined, and toDs(undefined) threw — reproducible as
  // soon as "one summary row per resource" was enabled on a flowchart with such a task.
  const getResourceSummaryRanges = (resource: string): Array<[number, number]> => {
    const allRanges: Array<[number, number]> = [];
    allTasksAdjusted.forEach(t => {
      const assignees = (t.assignee || '').split(',').map(n => n.trim());
      if (!assignees.includes(resource)) return;
      allRanges.push(...getResourceActiveDisplayRanges(t, resource));
    });
    allRanges.sort((a, b) => a[0] - b[0]);
    const merged: Array<[number, number]> = [];
    for (const r of allRanges) {
      const last = merged[merged.length - 1];
      if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
      else merged.push([...r]);
    }
    return merged;
  };

  // Renders one set of rows+bars+arrows for the given task list. Used once for the normal
  // (unfiltered / single-resource) view, and once per resource when multiple resources are
  // checked in the filter — each call is fully self-contained (own row indices, own arrows
  // scoped to tasks within `sectionTasks`), so the same task can safely appear in more than
  // one section. Row drag-to-reorder is disabled outside the normal view (allowDrag=false):
  // with a task potentially duplicated across sections, "reorder this row" is ambiguous.
  // `sectionResource` names the one resource this section represents (null in the normal,
  // unfiltered view) — it's what "Select specific resource times" edits, and what the
  // inactive-hours overlay is drawn for.
  const renderGanttSection = (baseTasks: typeof sortedTasksAdjusted, allowDrag: boolean, sectionResource: string | null, includeSubitems: boolean = false, includeAreaGroups: boolean = false, includeRegularBoxes: boolean = true, showExportCheckboxes: boolean = false) => {
    const ROW_H = 48;
    const ROW_CENTER = 20;
    // Interleave each parent's subitem rows directly after it, and/or group boxes
    // into their Area's toplevel row — shared with lib/svgExport.ts's export path via
    // lib/gantt.ts's buildExpandedGanttRows (see its own doc comment for why this is
    // centralized). Both are never independently reorderable (see topLevelIndexById/
    // expandedIndexForTopLevelIndex below), so this only ever runs for the plain,
    // unfiltered task list (never the resource-filtered/summary views, where both
    // flags stay false). sectionTasks keeps its original name (still used by every
    // existing piece of logic below — getBarCoords, ganttGridLines, the row .map —
    // completely unmodified, since they only ever cared about array index, not
    // hierarchy).
    const topLevelIndexById = new Map(baseTasks.map((t, i) => [t.id, i]));
    const isExpandingRows = includeSubitems || includeAreaGroups || !includeRegularBoxes;
    const sectionTasks: typeof sortedTasksAdjusted = isExpandingRows
      ? buildExpandedGanttRows<typeof sortedTasksAdjusted[number]>({
          topLevelTasks: baseTasks,
          areaByBoxId,
          getSubitemsFor: (t) => {
            // subitemGanttTasks' start comes straight out of lib/gantt.ts's
            // calcSubitemGanttTasks, in calcSchedule's raw, ganttStartDate-relative
            // schedule-column space — NOT the calendarOrigin-relative space every
            // other row here uses (`t.start`, from sortedTasksAdjusted, which went
            // through getAdjStart above). The two coincide only when calendarOrigin
            // equals ganttStartDate (no scheduledStart-pinned task anywhere in the
            // flowchart earlier than the project start); whenever they diverge —
            // any pinned task elsewhere pulls calendarOrigin earlier — every subitem
            // bar would render `originShift` columns away from where it belongs,
            // in practice usually far enough off-screen to look like it isn't
            // rendered at all. Re-anchor each subitem to the parent's own
            // already-correct adjusted start instead of trusting the two
            // independently-computed coordinate spaces to agree.
            const rawParentStart = ganttTasks.find(gt => gt.id === t.id)?.start ?? t.start;
            return subitemGanttTasks
              .filter(s => s.parentBoxId === t.id)
              .sort((a, b) => a.start - b.start)
              .map(s => ({ ...s, start: t.start + (s.start - rawParentStart) }));
          },
          makeAreaHeaderTask: (areaId, members) => {
            const area = areas.find(a => a.id === areaId);
            if (!area) return null;
            // Position each member in DISPLAY-column space, not raw schedule-column
            // space: an is24h (alwaysOn/is24_5) member's schedule column doesn't map
            // linearly to real time once chained (see is24hRealStarts's own comment
            // and every other bar-position computation in this file, which already
            // special-cases this) — comparing its raw `.start`/`.duration` directly
            // against a regular member's, as a naive Math.min/max would, put the
            // header's range at the wrong horizontal position. Observed as an
            // Area-toplevel bar for a group containing an is24_5 task rendering
            // shifted onto the next working hour instead of its real start.
            const dispRangeOf = (m: typeof members[number]): { left: number; right: number } => {
              if (showAllHours && (m.alwaysOn || m.is24_5)) {
                const real = is24hRealStarts.get(m.id);
                if (real) {
                  const rawTask = calcTaskMap[m.id];
                  const realEnd = rawTask ? is24hRealEnd(real, rawTask as any) : real;
                  return { left: realDateToDisplayCol(real), right: realDateToDisplayCol(realEnd) };
                }
              }
              const left = toDisplayPos(m.start);
              const right = m.duration > 0 ? toDisplayEndPos(m.start + m.duration) : left;
              return { left, right };
            };
            const ranges = members.map(dispRangeOf);
            const dispStart = Math.min(...ranges.map(r => r.left));
            const dispEnd = Math.max(...ranges.map(r => r.right));
            // Synthetic "task" for an Area's toplevel row — negative id, guaranteed to
            // never collide with a real box/subitem id (always positive, see
            // SESSION_ID_OFFSET in FlowchartBuilder.tsx), used everywhere below
            // (isAreaHeader = t.id < 0) instead of a new field, so the row-rendering
            // type stays exactly Box-shaped. start/duration here are DISPLAY-column
            // values already (see dispRangeOf above), not schedule-column ones — the
            // t.id < 0 render branch below must use them directly, never through
            // toDisplayPos/toDisplayEndPos again.
            return {
              id: -area.id, x: 0, y: 0, width: 0, height: 0, text: area.title || '', title: area.title || '',
              type: 'regular', departmentColor: area.color, status: 'none', assignee: '', completion: calcAreaCompletion(members),
              importantFinish: false, start: dispStart, duration: dispEnd - dispStart,
            };
          },
          includeSubitems, includeAreaGroups, includeRegularBoxes,
        })
      : baseTasks;
    // Sparse on purpose when not expanding rows — see its "length > 0 ? ... : ganttDropIdx"
    // fallback usage below, mirroring buildExpandedGanttRows' own passthrough case.
    const expandedIndexForTopLevelIndex: number[] = [];
    if (isExpandingRows) {
      baseTasks.forEach((t, i) => {
        const idx = sectionTasks.findIndex(e => e.id === t.id);
        if (idx !== -1) expandedIndexForTopLevelIndex[i] = idx;
      });
      expandedIndexForTopLevelIndex[baseTasks.length] = sectionTasks.length; // "drop at the very end"
    }
    // Only the section that can actually be drag-reordered needs to publish this —
    // see expandedIndexForTopLevelIndexRef's own declaration comment.
    if (allowDrag) expandedIndexForTopLevelIndexRef.current = expandedIndexForTopLevelIndex;
    const getBarCoords = (taskId: number) => {
      const task = sectionTasks.find(t2 => t2.id === taskId);
      const rowIdx = sectionTasks.findIndex(t2 => t2.id === taskId);
      if (!task || rowIdx === -1) return null;
      // showAllHours: an is24h task's schedule column doesn't linearly map to real
      // time once chained, so arrows must anchor to its actual real start/end —
      // same override as the bar-segment IIFE, otherwise dependency arrows point at
      // the work-hour grid's snapped day boundary instead of the bar itself.
      const realStart = showAllHours && (task.alwaysOn || task.is24_5) ? is24hRealStarts.get(task.id) : undefined;
      const rawTask = realStart ? calcTaskMap[task.id] : undefined;
      const leftDisp = realStart ? realDateToDisplayCol(realStart) : toDisplayPos(task.start);
      const rightDisp = (realStart && rawTask)
        ? realDateToDisplayCol(is24hRealEnd(realStart, rawTask as any))
        : (task.duration > 0 ? toDisplayEndPos(task.start + task.duration) : toDisplayPos(task.start + task.duration));
      return {
        leftX: (leftDisp - viewStartColIdx) * dw,
        rightX: (rightDisp - viewStartColIdx) * dw - 4,
        y: rowIdx * ROW_H + ROW_CENTER,
      };
    };
    const predArrows = selectedGanttTask !== null ? arrows.filter(a => a.toBox === selectedGanttTask) : [];
    const succArrows = selectedGanttTask !== null ? arrows.filter(a => a.fromBox === selectedGanttTask) : [];
    const selectedArrowIds = new Set([...predArrows, ...succArrows].map(a => a.id));
    const makePath = (x1: number, y1: number, x2: number, y2: number) => {
      const cx = Math.max(40, Math.abs(x2 - x1) / 2);
      return `M ${x1} ${y1} C ${x1 + cx} ${y1} ${x2 - cx} ${y2} ${x2} ${y2}`;
    };
    const ganttGridLines: React.ReactNode = (() => {
      const totalH = sectionTasks.length * ROW_H;
      if (totalH === 0) return null;
      const lines: React.ReactNode[] = [];
      let x = 0;
      if (visibleLevels.hours && calendarHeaders.hours?.length > 0) {
        for (const h of calendarHeaders.hours) {
          x += h.span * dw;
          lines.push(<line key={x} x1={x} y1={0} x2={x} y2={totalH} stroke="#e5e7eb" strokeWidth="1" />);
        }
      } else if (visibleLevels.days && calendarHeaders.days?.length > 0) {
        for (const day of calendarHeaders.days) {
          if (!day.isWeekday || day.isNonWorking) {
            lines.push(<rect key={`w${x}`} x={x} y={0} width={day.span * dw} height={totalH} fill="#f3f4f6" opacity="0.7" />);
          }
          x += day.span * dw;
          lines.push(<line key={x} x1={x} y1={0} x2={x} y2={totalH} stroke="#e5e7eb" strokeWidth="1" />);
        }
      } else if (visibleLevels.weeks && calendarHeaders.weeks?.length > 0) {
        for (const w of calendarHeaders.weeks) {
          x += w.span * dw;
          lines.push(<line key={x} x1={x} y1={0} x2={x} y2={totalH} stroke="#e5e7eb" strokeWidth="1" />);
        }
      } else if (visibleLevels.months && calendarHeaders.months?.length > 0) {
        for (const m of calendarHeaders.months) {
          x += m.span * dw;
          lines.push(<line key={x} x1={x} y1={0} x2={x} y2={totalH} stroke="#e5e7eb" strokeWidth="1" />);
        }
      } else if (visibleLevels.quarters && calendarHeaders.quarters?.length > 0) {
        for (const q of calendarHeaders.quarters) {
          x += q.span * dw;
          lines.push(<line key={x} x1={x} y1={0} x2={x} y2={totalH} stroke="#d1d5db" strokeWidth="1" />);
        }
      }
      if (lines.length === 0) return null;
      return (
        <svg className="gantt-grid-svg" style={{ position: 'absolute', top: 0, left: ganttLabelWidth, pointerEvents: 'none', zIndex: 1 }} width={renderColCount * dw + 40} height={totalH}>
          {lines}
        </svg>
      );
    })();

    return (<>
    <div style={{ position: 'relative', background: 'white' }} onClick={() => { setSelectedGanttTask(null); setEditingResourceCell(null); }}>
      {ganttGridLines}
      {(showAllGanttDeps || selectedGanttTask !== null || (showCriticalPath && criticalPathIds.size > 0)) && (
        <svg
          className="gantt-deps-svg"
          style={{ position: 'absolute', top: 0, left: ganttLabelWidth, pointerEvents: 'none', overflow: 'hidden', zIndex: 10 }}
          width={renderColCount * dw + 40}
          height={sectionTasks.length * ROW_H}
        >
          <defs>
            <marker id="arrow-all" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill="#94a3b8" />
            </marker>
            <marker id="arrow-pred" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill="#3b82f6" />
            </marker>
            <marker id="arrow-succ" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill="#f59e0b" />
            </marker>
            <marker id="arrow-critical" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill="#ef4444" />
            </marker>
          </defs>
          {showAllGanttDeps && arrows.filter(a => !selectedArrowIds.has(a.id) && !criticalPathArrowIds.has(a.id)).map(a => {
            const from = getBarCoords(a.fromBox);
            const to = getBarCoords(a.toBox);
            if (!from || !to) return null;
            return <path key={`all-${a.id}`} d={makePath(from.rightX, from.y, to.leftX, to.y)} fill="none" stroke="#94a3b8" strokeWidth="1.5" markerEnd="url(#arrow-all)" />;
          })}
          {predArrows.map(a => {
            const from = getBarCoords(a.fromBox);
            const to = getBarCoords(a.toBox);
            if (!from || !to) return null;
            return <path key={`pred-${a.id}`} d={makePath(from.rightX, from.y, to.leftX, to.y)} fill="none" stroke="#3b82f6" strokeWidth="2" markerEnd="url(#arrow-pred)" />;
          })}
          {succArrows.map(a => {
            const from = getBarCoords(a.fromBox);
            const to = getBarCoords(a.toBox);
            if (!from || !to) return null;
            return <path key={`succ-${a.id}`} d={makePath(from.rightX, from.y, to.leftX, to.y)} fill="none" stroke="#f59e0b" strokeWidth="2" markerEnd="url(#arrow-succ)" />;
          })}
          {showCriticalPath && [...criticalPathArrowIds].map(aid => {
            const a = arrows.find(ar => ar.id === aid);
            if (!a) return null;
            const from = getBarCoords(a.fromBox);
            const to = getBarCoords(a.toBox);
            if (!from || !to) return null;
            return <path key={`cp-${aid}`} d={makePath(from.rightX, from.y, to.leftX, to.y)} fill="none" stroke="#ef4444" strokeWidth="2.5" markerEnd="url(#arrow-critical)" />;
          })}
        </svg>
      )}
      <div ref={allowDrag ? ganttTasksContainerRef : undefined} style={{ position: 'relative', zIndex: 2 }}>
        {allowDrag && ganttDragIdx !== null && ganttDropIdx !== null && (
          <div style={{ position: 'absolute', left: 0, right: 0, top: (expandedIndexForTopLevelIndex.length > 0 ? expandedIndexForTopLevelIndex[ganttDropIdx] : ganttDropIdx) * 48 - 2, height: 3, background: '#3b82f6', borderRadius: 2, zIndex: 20, pointerEvents: 'none' }} />
        )}
        {(() => {
          const calloutBoxIds = new Set(boxes.filter(b => b.type === 'callout').map(b => b.id));
          const ganttConflictIds = new Set<number>(
            sectionTasks
              .filter(t => t.scheduledStart)
              .filter(t => arrows
                .filter(a => a.toBox === t.id && !calloutBoxIds.has(a.fromBox))
                .map(a => sectionTasks.find(p => p.id === a.fromBox))
                .filter(Boolean)
                .some(p => getEndCol(p!.id) > t.start)
              )
              .map(t => t.id)
          );

          return sectionTasks.map((t, idx) => {
            // Area toplevel row (t.id < 0, see buildExpandedGanttRows' makeAreaHeaderTask
            // callback above) — a wholly separate, much simpler render than a normal
            // task row below: no drag
            // handle, no status/resource/critical-path decorations, no dependency
            // arrows (none ever reference a negative id), just a bold label and one
            // solid, clearly-distinguished bar spanning its members' full range.
            if (t.id < 0) {
              // t.start/t.duration are already DISPLAY-column values here (see
              // buildExpandedGanttRows' makeAreaHeaderTask callback above) — unlike
              // every other row, do NOT run them through toDisplayPos/toDisplayEndPos
              // again, that's only valid for raw schedule-column values.
              const leftDisp = t.start;
              const rightDisp = t.start + t.duration;
              const leftX = (leftDisp - viewStartColIdx) * dw;
              const rightX = (rightDisp - viewStartColIdx) * dw;
              return (
                <div key={t.id} className="flex items-center mb-2" style={{ userSelect: 'none' }}>
                  <div className="gantt-label-cell" style={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', flexShrink: 0, width: ganttLabelWidth, position: 'relative', background: 'white', zIndex: 25, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ width: 18, flexShrink: 0 }} />
                    <div className="text-sm truncate" style={{ width: ganttLabelWidth - 18, flexShrink: 0, fontWeight: 800, color: '#111827' }}>{t.text}</div>
                  </div>
                  {renderDateCell(undefined, 'start')}
                  {renderDateCell(undefined, 'end')}
                  <div className="gantt-bars-cell relative" style={{ height: 40, width: renderColCount * dw, flexGrow: 0, flexShrink: 0, overflow: 'hidden' }}>
                    <div className="absolute inset-0 flex items-center">
                      {[...Array(renderColCount)].map((_, i) => (
                        <div key={i} className="border-l border-gray-200" style={{ width: dw, height: 40, background: isNonWorkDisplayCol(i + viewStartColIdx) ? '#f3f4f6' : undefined }} />
                      ))}
                    </div>
                    {(() => {
                      // t.completion here is the duration-weighted average of this
                      // Area's member boxes (see lib/gantt.ts's calcAreaCompletion,
                      // computed in makeAreaHeaderTask above) — fully green at 100%
                      // instead of the Area's own color, same "done" green
                      // (#10b981) CanvasArea.tsx's boundary and CanvasBox.tsx's
                      // completion ring use.
                      const areaCompletionPct = t.completion || 0;
                      const isAreaComplete = areaCompletionPct >= 100;
                      const barWidth = Math.max(rightX - leftX, 4);
                      return (
                        <div
                          className="absolute rounded"
                          style={{ left: leftX, width: barWidth, top: 4, bottom: 4, background: isAreaComplete ? '#10b981' : (t.departmentColor || '#94a3b8'), border: isAreaComplete ? '2px solid #047857' : '2px solid rgba(0,0,0,0.25)', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', overflow: 'hidden' }}
                        >
                          {!isAreaComplete && areaCompletionPct > 0 && (
                            <div
                              className="absolute top-0 left-0 h-full"
                              style={{ width: `${Math.min(areaCompletionPct, 100)}%`, backgroundColor: 'rgba(0, 0, 0, 0.2)', borderRight: '2px solid rgba(0, 0, 0, 0.3)' }}
                            />
                          )}
                          {areaCompletionPct > 0 && (
                            <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold" style={{ color: isAreaComplete ? 'white' : '#111827' }}>
                              {areaCompletionPct}%
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            }
            const isSubitem = t.parentBoxId !== undefined;
            const topLevelIdx = topLevelIndexById.get(t.id);
            return (
            <div
              key={t.id}
              className="flex items-center mb-2"
              style={{ opacity: allowDrag && !isSubitem && ganttDragIdx === topLevelIdx ? 0.4 : 1, userSelect: 'none' }}
            >
              <div className="gantt-label-cell" style={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', flexShrink: 0, width: ganttLabelWidth, position: 'relative', background: 'white', zIndex: 25, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}>
                {showExportCheckboxes && !isSubitem && (
                  <input
                    type="checkbox"
                    checked={exportSelectedBoxIds.includes(t.id)}
                    onChange={(e) => {
                      setExportSelectedBoxIds(
                        e.target.checked
                          ? [...exportSelectedBoxIds, t.id]
                          : exportSelectedBoxIds.filter(id => id !== t.id)
                      );
                    }}
                    title={tr('onlyExportSelectedRows')}
                    className="cursor-pointer"
                    style={{ flexShrink: 0, marginRight: 4 }}
                  />
                )}
                {allowDrag && !isSubitem ? (
                  <div
                    onMouseDown={(e) => startGanttDrag(e, topLevelIdx!)}
                    style={{ width: 18, flexShrink: 0, cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14, lineHeight: 1 }}
                    title={tr('dragToReorder')}
                  >
                    ⠿
                  </div>
                ) : (
                  <div style={{ width: 18, flexShrink: 0 }} />
                )}
                <div
                  className="text-sm truncate"
                  style={{ width: ganttLabelWidth - 18 - (isSubitem ? 16 : 0) - (showExportCheckboxes && !isSubitem ? 20 : 0), flexShrink: 0, marginLeft: isSubitem ? 16 : 0, color: isSubitem ? '#6b7280' : undefined, fontSize: isSubitem ? 12 : undefined }}
                >{t.text}</div>
              </div>
              {renderDateCell(isSubitem ? undefined : t.id, 'start')}
              {renderDateCell(isSubitem ? undefined : t.id, 'end')}
              <div className="gantt-bars-cell relative" style={{ height: 40, width: renderColCount * dw, flexGrow: 0, flexShrink: 0, overflow: 'hidden' }}>
                <div className="absolute inset-0 flex items-center">
                  {[...Array(renderColCount)].map((_, i) => (
                    <div key={i} className="border-l border-gray-200" style={{ width: dw, height: 40, background: isNonWorkDisplayCol(i + viewStartColIdx) ? '#f3f4f6' : undefined }} />
                  ))}
                </div>
                {(() => {
                  // "Select specific resource times" edit mode for this task/resource: replace
                  // the normal bar with a strip of toggle cells, one per displayed unit of the
                  // task's duration, instead of computing segments/fill as usual.
                  if (editingResourceCell && editingResourceCell.taskId === t.id && editingResourceCell.resource === sectionResource) {
                    const resource = editingResourceCell.resource;
                    // Mirror the is24h real-time override used by the normal bar below (see the
                    // matching comment there): an is24h task's schedule columns are compressed
                    // workday-equivalents, not real hours, so subdividing by raw t.duration/
                    // toDisplayPos would misplace and mis-size the cells. realStart set here means
                    // "use real clock time for both cell count and positions", exactly like the
                    // normal bar's dispStartOverride path.
                    const is24hRealStart = showAllHours && (t.alwaysOn || t.is24_5) ? is24hRealStarts.get(t.id) : undefined;
                    const rawTaskForEdit = is24hRealStart ? calcTaskMap[t.id] : undefined;
                    const editDuration = is24hRealStart && rawTaskForEdit ? (rawTaskForEdit.duration ?? t.duration) : t.duration;
                    const n = resourceHoursCellCount(editDuration);
                    const cells = getResourceActiveCells({ duration: editDuration, resourceHours: t.resourceHours }, resource);
                    // Each cell's own [left, right] pixel span (possibly MORE than one span
                    // per cell — see buildResourceCellSegments/splitRangeAtNightGaps above —
                    // when a cell's real span crosses a workday boundary) — computed from
                    // where that specific hour/day actually falls (via the same position
                    // functions the normal bar uses), not by dividing the bar's overall pixel
                    // width evenly. The two can differ a lot once non-working hours/days are
                    // part of the span.
                    const editBarEndDisp = editDuration > 0 ? toDisplayEndPos(t.start + editDuration) : toDisplayPos(t.start + editDuration);
                    const cellSegs = buildResourceCellSegments(t.start, editDuration, n, is24hRealStart && rawTaskForEdit ? is24hRealStart : undefined, editBarEndDisp);
                    const cellRanges = cellSegs.map(seg => ({
                      cellIndex: seg.cellIndex,
                      isLastSegOfCell: seg.isLastSegOfCell,
                      left: (seg.left - viewStartColIdx) * dw,
                      right: (seg.right - viewStartColIdx) * dw,
                    }));
                    const editLeftPx = Math.min(...cellRanges.map(r => r.left));
                    const editRightPx = Math.max(...cellRanges.map(r => r.right));
                    const editWidthPx = Math.max(editRightPx - editLeftPx - 4, 4);
                    return (
                      <div
                        className="absolute top-1/2 transform -translate-y-1/2 rounded overflow-hidden"
                        style={{ left: editLeftPx, width: editWidthPx, height: 32, border: '2px solid #16697A' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {cellRanges.map((range, i) => (
                          <div
                            key={i}
                            onClick={() => {
                              const next = [...cells];
                              next[range.cellIndex] = !next[range.cellIndex];
                              onResourceHoursChange(t.id, resource, next);
                            }}
                            title={`${resource} — ${tr('cell')} ${range.cellIndex + 1}/${n}`}
                            style={{
                              position: 'absolute',
                              left: range.left - editLeftPx,
                              width: Math.max(range.right - range.left, 1),
                              height: '100%',
                              background: cells[range.cellIndex] ? (t.departmentColor || '#3b82f6') : 'transparent',
                              borderRight: (range.isLastSegOfCell && range.cellIndex < n - 1) ? '1px solid rgba(0,0,0,0.25)' : undefined,
                              boxSizing: 'border-box',
                              cursor: 'pointer',
                            }}
                          />
                        ))}
                      </div>
                    );
                  }
                  // showAllHours: an is24h task's schedule column doesn't linearly map to
                  // real time once chained, so its bar is placed from its actual real
                  // start/end instead of the schedule-column-based toDisplayPos.
                  let dispStartOverride: number | null = null;
                  let dispEndOverride: number | null = null;
                  if (showAllHours && (t.alwaysOn || t.is24_5)) {
                    const realStart = is24hRealStarts.get(t.id);
                    // Use the original calcSchedule duration (real clock hours), not
                    // t.duration — adjustedTasks overwrites that to a schedule-column
                    // width (endCol - s) for is24h tasks, which is a different unit.
                    const rawTask = calcTaskMap[t.id];
                    if (realStart && rawTask) {
                      const realEnd = is24hRealEnd(realStart, rawTask as any);
                      dispStartOverride = realDateToDisplayCol(realStart);
                      dispEndOverride = realDateToDisplayCol(realEnd);
                    }
                  }
                  const barEndDisp = dispEndOverride ?? (t.duration > 0 ? toDisplayEndPos(t.start + t.duration) : toDisplayPos(t.start + t.duration));
                  // For the on-bar label: t.duration is adjustedTasks' overwritten
                  // schedule-column width for is24h tasks (endCol - s), which can
                  // collapse toward 0 — floored to the 0.5 safety minimum — when the
                  // task's real span falls entirely outside work hours (both ends snap
                  // to the same "next work morning" grid column). The true duration is
                  // calcSchedule's own value, in calcTaskMap.
                  const displayDuration = (t.alwaysOn || t.is24_5) ? (calcTaskMap[t.id]?.duration ?? t.duration) : t.duration;
                  // When filtered to one resource, and "Select specific resource times" only
                  // covers part of this task, show the time THAT resource actually spends on
                  // it instead of the task's full duration — the number on the bar should match
                  // what this specific person will work on it, not the whole task's length.
                  const resourceActiveDuration = (() => {
                    if (!sectionResource) return displayDuration;
                    const n = resourceHoursCellCount(displayDuration);
                    const activeCells = getResourceActiveCells({ duration: displayDuration, resourceHours: t.resourceHours }, sectionResource);
                    if (activeCells.every(Boolean)) return displayDuration; // fully active — avoid rounding noise
                    // Sum each active cell's OWN duration rather than (activeCount / n) *
                    // displayDuration — cells aren't all equal-sized once displayDuration isn't
                    // a whole number of resourceCellUnit (see resourceHoursCellCount's comment);
                    // the proportional shortcut silently assumed they were, e.g. showing 8.63h
                    // instead of 9h for an 11.5h task with its last 2.5h deselected.
                    let total = 0;
                    for (let i = 0; i < n; i++) {
                      if (activeCells[i]) total += resourceCellOffset(displayDuration, i + 1) - resourceCellOffset(displayDuration, i);
                    }
                    return Math.round(total * 100) / 100;
                  })();
                  const resourceActiveDurationDisplay = Math.round(convertDurationUnit(resourceActiveDuration, durationUnit, ganttDurationDisplay, hoursPerDay) * 100) / 100;
                  // Split at weekend gaps for non-24/7 tasks in days mode when weekends are visible
                  const shouldSplit = dispStartOverride === null && !t.alwaysOn && schedWHO && !workHoursOnly && durationUnit === 'days' && t.duration > 0;
                  const segs: Array<{ dispStart: number; dispEnd: number }> = [];
                  if (shouldSplit) {
                    const startIdx = Math.floor(t.start);
                    const endIdx = Math.floor(t.start + t.duration);
                    let segSchedStart = t.start;
                    for (let si = startIdx; si < Math.min(endIdx, colList.length - 1); si++) {
                      const curDisp = displayColMap.get(toDs(colList[si])) ?? si;
                      const nxtDisp = displayColMap.get(toDs(colList[si + 1])) ?? (si + 1);
                      if (nxtDisp > curDisp + 1) {
                        segs.push({ dispStart: toDisplayPos(segSchedStart), dispEnd: curDisp + 1 });
                        segSchedStart = si + 1;
                      }
                    }
                    if (segSchedStart < t.start + t.duration) {
                      segs.push({ dispStart: toDisplayPos(segSchedStart), dispEnd: barEndDisp });
                    }
                  }
                  // Split at night gaps for regular (non-is24h) tasks in hours mode when
                  // non-working hours are visible — otherwise a task spanning into the next
                  // work day would render as one solid bar bleeding through the night,
                  // instead of stopping at end-of-day and resuming next work morning.
                  const shouldSplitHours = dispStartOverride === null && durationUnit === 'hours' && showAllHours && t.duration > 0;
                  if (shouldSplitHours) {
                    const endSched = t.start + t.duration;
                    let segSchedStart = t.start;
                    let boundary = Math.floor(segSchedStart / hoursPerDay) * hoursPerDay + hoursPerDay;
                    const nightGap = 24 - hoursPerDay;
                    while (boundary < endSched) {
                      segs.push({ dispStart: toDisplayPos(segSchedStart), dispEnd: toDisplayPos(boundary) - nightGap });
                      segSchedStart = boundary;
                      boundary += hoursPerDay;
                    }
                    if (segSchedStart < endSched) {
                      segs.push({ dispStart: toDisplayPos(segSchedStart), dispEnd: barEndDisp });
                    }
                  }
                  if (segs.length === 0) segs.push({ dispStart: dispStartOverride ?? toDisplayPos(t.start), dispEnd: barEndDisp });
                  const totalDispW = segs.reduce((s, sg) => s + Math.max(sg.dispEnd - sg.dispStart, 0), 0);
                  const outlineStyle = selectedGanttTask === t.id ? '3px solid #1d4ed8' : ganttConflictIds.has(t.id) ? '3px solid #ef4444' : (showCriticalPath && criticalPathIds.has(t.id)) ? '3px solid #ef4444' : undefined;
                  const completionPct = t.completion || 0;
                  return segs.map((seg, si) => {
                    const segW = Math.max(seg.dispEnd - seg.dispStart, 0);
                    const prevW = segs.slice(0, si).reduce((s, sg) => s + Math.max(sg.dispEnd - sg.dispStart, 0), 0);
                    const segStartFrac = totalDispW > 0 ? prevW / totalDispW : 0;
                    const segEndFrac = totalDispW > 0 ? (prevW + segW) / totalDispW : 1;
                    let segCompPct = 0;
                    if (completionPct > 0 && segW > 0) {
                      const fillEnd = Math.min(completionPct / 100, segEndFrac);
                      if (fillEnd > segStartFrac) {
                        segCompPct = (fillEnd - segStartFrac) / (segEndFrac - segStartFrac) * 100;
                      }
                    }
                    return (
                      <div
                        key={si}
                        onClick={(e) => { e.stopPropagation(); setSelectedGanttTask(selectedGanttTask === t.id ? null : t.id); setEditingResourceCell(null); }}
                        onContextMenu={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          setBarContextMenu({ taskId: t.id, top: e.clientY, left: e.clientX, resource: sectionResource });
                        }}
                        className="absolute top-1/2 transform -translate-y-1/2 rounded shadow-md overflow-hidden"
                        style={{
                          left: (seg.dispStart - viewStartColIdx) * dw,
                          width: Math.max(segW * dw - 4, 4),
                          height: 32,
                          backgroundColor: t.departmentColor || '#3b82f6',
                          cursor: 'pointer',
                          outline: outlineStyle,
                          outlineOffset: '2px',
                        }}
                      >
                        {segCompPct > 0 && (
                          <div
                            className="absolute top-0 left-0 h-full"
                            style={{ width: `${segCompPct}%`, backgroundColor: 'rgba(0, 0, 0, 0.2)', borderRight: segCompPct < 100 ? '2px solid rgba(0, 0, 0, 0.3)' : 'none' }}
                          />
                        )}
                        {si === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center text-gray-900 text-xs font-semibold">
                            {resourceActiveDurationDisplay} {ganttDurationDisplay === 'hours' ? 'h' : ganttDurationDisplay === 'weeks' ? 'w' : 'd'}
                            {completionPct > 0 && (
                              <span className="ml-1 text-[10px]">({completionPct}%)</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
                {sectionResource && t.resourceHours?.[sectionResource] && !(editingResourceCell && editingResourceCell.taskId === t.id && editingResourceCell.resource === sectionResource) && (() => {
                  // Overlay a hatched mask over the stretches of the bar where this section's
                  // resource is NOT active (per "Select specific resource times"), so the full
                  // task boundary still shows but only the active hours read as filled. Same
                  // is24h real-time handling as the editor above (and the normal bar below) —
                  // see the matching comment there.
                  const is24hRealStartMask = showAllHours && (t.alwaysOn || t.is24_5) ? is24hRealStarts.get(t.id) : undefined;
                  const rawTaskForMask = is24hRealStartMask ? calcTaskMap[t.id] : undefined;
                  const maskDuration = is24hRealStartMask && rawTaskForMask ? (rawTaskForMask.duration ?? t.duration) : t.duration;
                  const n = resourceHoursCellCount(maskDuration);
                  const cells = getResourceActiveCells({ duration: maskDuration, resourceHours: t.resourceHours }, sectionResource);
                  const maskBarEndDisp = maskDuration > 0 ? toDisplayEndPos(t.start + maskDuration) : toDisplayPos(t.start + maskDuration);
                  const segs = buildResourceCellSegments(t.start, maskDuration, n, is24hRealStartMask && rawTaskForMask ? is24hRealStartMask : undefined, maskBarEndDisp)
                    .map(seg => ({ ...seg, left: (seg.left - viewStartColIdx) * dw, right: (seg.right - viewStartColIdx) * dw }));
                  // Merge consecutive INACTIVE segments into hatch ranges — same rule as the
                  // active-range merge in getResourceActiveDisplayRanges above: never merge
                  // two segments of the SAME cell (a night-gap split), only a genuine
                  // cell-to-cell boundary.
                  const ranges: Array<{ left: number; right: number }> = [];
                  let curLeft: number | null = null;
                  let curRight: number | null = null;
                  for (let k = 0; k < segs.length; k++) {
                    const seg = segs[k];
                    if (cells[seg.cellIndex]) {
                      if (curLeft !== null) { ranges.push({ left: curLeft, right: curRight! }); curLeft = null; curRight = null; }
                      continue;
                    }
                    if (curLeft === null) { curLeft = seg.left; curRight = seg.right; continue; }
                    const prevSeg = segs[k - 1];
                    const continuesPrevCell = prevSeg.isLastSegOfCell && prevSeg.cellIndex === seg.cellIndex - 1;
                    if (continuesPrevCell) curRight = seg.right;
                    else { ranges.push({ left: curLeft, right: curRight! }); curLeft = seg.left; curRight = seg.right; }
                  }
                  if (curLeft !== null) ranges.push({ left: curLeft, right: curRight! });
                  return ranges.map((r, i) => (
                    <div
                      key={i}
                      className="absolute top-1/2 transform -translate-y-1/2 pointer-events-none"
                      style={{
                        left: r.left,
                        width: Math.max(r.right - r.left, 1),
                        height: 32,
                        background: 'rgba(255,255,255,0.65)',
                        backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.08) 0px, rgba(0,0,0,0.08) 4px, transparent 4px, transparent 8px)',
                      }}
                    />
                  ));
                })()}
                {t.importantFinish && (() => {
                  // See the matching is24h real-time override in the bar-segment IIFE above.
                  const is24hIF = showAllHours && (t.alwaysOn || t.is24_5) ? is24hRealStarts.get(t.id) : undefined;
                  const ifCol = is24hIF
                    ? realDateToDisplayCol(is24hRealEnd(is24hIF, calcTaskMap[t.id] as any))
                    : (t.duration > 0 ? toDisplayEndPos(t.start + t.duration) : toDisplayPos(t.start + t.duration));
                  return (
                    <div className="absolute top-1/2 transform -translate-y-1/2" style={{ left: (ifCol - viewStartColIdx) * dw - 2 }}>
                      <div className="bg-purple-500 text-white" style={{ width: '16px', height: '16px', transform: 'rotate(45deg)' }}></div>
                    </div>
                  );
                })()}
                {t.scheduledStart && (() => {
                  // is24h: use the real pin/chain-derived clock time directly, bypassing
                  // the work-hour grid (dateToHourGridCol can't represent a pin outside
                  // the configured work window — e.g. an overnight is24/5 start — see the
                  // matching bar-segment override above).
                  const is24hSd = showAllHours && (t.alwaysOn || t.is24_5) ? is24hRealStarts.get(t.id) : undefined;
                  const sdCol = is24hSd ? realDateToDisplayCol(is24hSd) : toDisplayPos(t.start);
                  return (
                    <div
                      className="absolute flex flex-col items-center"
                      style={{ left: (sdCol - viewStartColIdx) * dw, top: 0, bottom: 0, pointerEvents: 'none', zIndex: 6 }}
                      title={tr('setDateMarker', { date: t.scheduledStart.split('T')[0] })}
                    >
                      <div style={{ width: 2, height: '100%', background: 'repeating-linear-gradient(to bottom, #1e3a8a 0px, #1e3a8a 4px, transparent 4px, transparent 8px)' }} />
                      <div className="absolute top-0 left-1" style={{ color: '#1e3a8a', fontSize: 10, whiteSpace: 'nowrap', lineHeight: 1 }}>
                        {t.scheduledStart.split('T')[0]}
                      </div>
                    </div>
                  );
                })()}
                {t.earliestStart && (() => {
                  const esColSched = !t.earliestStart.startsWith('W')
                    ? dateToCol(parseD(t.earliestStart))
                    : calcEarliestStartOffset(t) + originShift;
                  const esCol = toDisplayPos(esColSched);
                  if (esCol <= 0) return null;
                  return (
                    <div
                      className="absolute flex flex-col items-center"
                      style={{ left: (esCol - viewStartColIdx) * dw, top: 0, bottom: 0, pointerEvents: 'none', zIndex: 5 }}
                      title={tr('earliestStartMarker', { value: t.earliestStart })}
                    >
                      <div style={{ width: 2, height: '100%', background: 'repeating-linear-gradient(to bottom, #3b82f6 0px, #3b82f6 4px, transparent 4px, transparent 8px)' }} />
                      <div className="absolute top-0 left-1 text-blue-500" style={{ fontSize: 10, whiteSpace: 'nowrap', lineHeight: 1 }}>
                        {t.earliestStart.includes('T')
                          ? `${t.earliestStart.split('T')[0]} ${t.earliestStart.split('T')[1]}`
                          : t.earliestStart}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            );
          });
        })()}
      </div>
    </div>
    </>);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="w-full bg-blue-50 flex flex-col" style={{ height: '100dvh' }}>
      <div className="bg-white shadow" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12, overflow: 'hidden' }}>
        <h1 className="text-xl font-bold text-gray-800" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{tr('ganttChartTitle', { title })}</h1>
        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
        <div className="flex items-center gap-3" style={{ minWidth: 'max-content' }}>

          {/* Timeline button + popup */}
          <button
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setTimelinePopupPos({ top: r.bottom + 4, left: r.left });
              setShowTimelinePopup(v => !v);
            }}
            className="flex items-center gap-1.5 px-3 py-1 border rounded text-xs font-semibold bg-gray-50 hover:bg-gray-100"
            style={showTimelinePopup ? { borderColor: '#489FB5', background: '#e6f4f5', color: '#16697A' } : {}}
          >
            {tr('timelineBtn')} ▾
          </button>

          {/* View button + popup — display/visibility toggles that used to sit in
              the bottom section of the Timeline popup above (hide non-working
              days/hours, show subitems), split out into their own menu. */}
          <button
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setViewPopupPos({ top: r.bottom + 4, left: r.left });
              setShowViewPopup(v => !v);
            }}
            className="flex items-center gap-1.5 px-3 py-1 border rounded text-xs font-semibold bg-gray-50 hover:bg-gray-100"
            style={showViewPopup ? { borderColor: '#489FB5', background: '#e6f4f5', color: '#16697A' } : {}}
          >
            {tr('showOptionsBtn')} ▾
          </button>

          {/* Duration display unit — label-only setting for bar duration text; does not affect scheduling */}
          <label className="flex items-center gap-1.5 px-3 py-1 border rounded text-xs font-semibold bg-gray-50 select-none">
            {tr('ganttDurationLabel')}
            <select
              value={ganttDurationDisplay}
              onChange={(e) => setGanttDurationDisplay(e.target.value)}
              className="bg-transparent border-none text-xs font-semibold cursor-pointer"
              style={{ outline: 'none' }}
            >
              <option value="hours">{tr('hours')}</option>
              <option value="days">{tr('days')}</option>
              <option value="weeks">{tr('weeks')}</option>
            </select>
          </label>

          <label className="flex items-center gap-2 px-3 py-1 border rounded bg-gray-50 text-xs cursor-pointer font-semibold select-none">
            <input type="checkbox" checked={showAllGanttDeps} onChange={(e) => setShowAllGanttDeps(e.target.checked)} className="cursor-pointer" />
            {tr('showAllDependencies')}
          </label>

          <label
            className="flex items-center gap-2 px-3 py-1 border rounded text-xs cursor-pointer font-semibold select-none"
            style={showCriticalPath ? { borderColor: '#ef4444', background: '#fef2f2', color: '#dc2626' } : { background: '#f9fafb' }}
          >
            <input type="checkbox" checked={showCriticalPath} onChange={(e) => setShowCriticalPath(e.target.checked)} className="cursor-pointer" />
            {tr('criticalPath')}
          </label>

          <button onClick={onClose} className="bg-blue-500 text-white px-3 py-1 rounded text-sm" style={{ flexShrink: 0 }}>
            {tr('backToFlowPlanner')}
          </button>

          <button
            onClick={() => {
              const tasks2 = ganttSortedTasksRef.current;
              const taskIds = new Set(tasks2.map(t => t.id));
              const ganttArrows = arrows.filter((a: Arrow) => taskIds.has(a.fromBox) && taskIds.has(a.toBox));

              const successors = new Map<number, number[]>();
              const predecessors = new Map<number, Set<number>>();
              for (const t of tasks2) { successors.set(t.id, []); predecessors.set(t.id, new Set()); }
              for (const a of ganttArrows) {
                successors.get(a.fromBox)!.push(a.toBox);
                predecessors.get(a.toBox)!.add(a.fromBox);
              }

              const depth = new Map<number, number>();
              const getDepth = (id: number): number => {
                if (depth.has(id)) return depth.get(id)!;
                const succs = successors.get(id) || [];
                const d = succs.length === 0 ? 0 : 1 + Math.max(...succs.map(getDepth));
                depth.set(id, d); return d;
              };
              tasks2.forEach(t => getDepth(t.id));

              const placed = new Set<number>();
              const result: number[] = [];

              const tryPlace = (id: number) => {
                if (placed.has(id)) return;
                for (const pred of predecessors.get(id)!) { if (!placed.has(pred)) return; }
                placed.add(id);
                result.push(id);
                const sorted = [...(successors.get(id) || [])].sort((a, b) => getDepth(a) - getDepth(b));
                for (const succ of sorted) tryPlace(succ);
              };

              for (const t of tasks2) { if (predecessors.get(t.id)!.size === 0) tryPlace(t.id); }
              for (const t of tasks2) {
                if (!placed.has(t.id)) {
                  placed.add(t.id); result.push(t.id);
                  const sorted = [...(successors.get(t.id) || [])].sort((a, b) => getDepth(a) - getDepth(b));
                  for (const succ of sorted) tryPlace(succ);
                }
              }
              onGanttOrderChange(result);
              // Same reasoning as the manual-drag commit above: an explicit reorder
              // action should win outright, not be overridden by a stale preset preview.
              setExportOrderOverride(null);
            }}
            className="px-3 py-1 border rounded text-xs font-semibold bg-gray-50 hover:bg-gray-100"
            title={tr('reorderArrowHint')}
          >
            {tr('autoSort')}
          </button>

          {/* Set timeline dates button */}
          <button
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setTimelineDatesPopupPos({ top: r.bottom + 4, left: r.right });
              setViewStartInput(timelineView?.start ?? '');
              setViewEndInput(timelineView?.end ?? '');
              setShowTimelineDatesPopup(v => !v);
            }}
            className="flex items-center gap-1.5 px-3 py-1 border rounded text-xs font-semibold bg-gray-50 hover:bg-gray-100"
            style={timelineView ? { borderColor: '#489FB5', background: '#e6f4f5', color: '#16697A' } : {}}
          >
            {tr('setTimelineDates')}{timelineView ? ' ✓' : ''}
          </button>

          {/* Resource filter button + popup */}
          {resources.length > 0 && (
            <button
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setResourceFilterPopupPos({ top: r.bottom + 4, left: r.left });
                setShowResourceFilterPopup(v => !v);
              }}
              className="flex items-center gap-1.5 px-3 py-1 border rounded text-xs font-semibold bg-gray-50 hover:bg-gray-100"
              style={resourceFilter.length > 0 ? { borderColor: '#489FB5', background: '#e6f4f5', color: '#16697A' } : {}}
            >
              {tr('filterByResource')}{resourceFilter.length > 0 ? ` (${resourceFilter.length})` : ''} ▾
            </button>
          )}

          {/* Category filter button + popup */}
          {departments.length > 0 && (
            <button
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setCategoryFilterPopupPos({ top: r.bottom + 4, left: r.left });
                setShowCategoryFilterPopup(v => !v);
              }}
              className="flex items-center gap-1.5 px-3 py-1 border rounded text-xs font-semibold bg-gray-50 hover:bg-gray-100"
              style={categoryFilter.length > 0 ? { borderColor: '#489FB5', background: '#e6f4f5', color: '#16697A' } : {}}
            >
              {tr('filterByCategory')}{categoryFilter.length > 0 ? ` (${categoryFilter.length})` : ''} ▾
            </button>
          )}

          {/* Filtered Export button + popup — placed before the plain Export button
              since it configures what that button is about to export. */}
          <button
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setFilteredExportPopupPos({ top: r.bottom + 4, left: r.left });
              setShowFilteredExportPopup(v => !v);
            }}
            className="flex items-center gap-1.5 px-3 py-1 border rounded text-xs font-semibold bg-gray-50 hover:bg-gray-100"
            style={filteredExportEnabled ? { borderColor: '#489FB5', background: '#e6f4f5', color: '#16697A' } : {}}
          >
            {tr('filteredExport')}{filteredExportEnabled ? ` (${exportSelectedBoxIds.length})` : ''} ▾
          </button>

          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setGanttExportMenuPos({ top: r.bottom + 4, left: r.left });
                setShowGanttExportMenu(v => !v);
              }}
              className="flex items-center gap-1.5 px-3 py-1 border rounded text-xs font-semibold bg-gray-50 hover:bg-gray-100"
              title={tr('exportGantt')}
            >
              <Download size={13} /> {tr('export')}
            </button>
          </div>

        </div>
        </div>
      </div>

      <div
        ref={ganttScrollRef}
        className="flex-1 overflow-auto p-4"
        style={{ background: 'white', paddingLeft: 0 }}
        onTouchStart={(e) => {
          if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            ganttPinchRef.current = { dist: Math.hypot(dx, dy) };
          }
        }}
        onTouchMove={(e) => {
          if (e.touches.length === 2 && ganttPinchRef.current) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const newDist = Math.hypot(dx, dy);
            const ratio = newDist / ganttPinchRef.current.dist;
            setGanttZoom(z => Math.min(10, Math.max(0.2, z * ratio)));
            ganttPinchRef.current = { dist: newDist };
          }
        }}
        onTouchEnd={() => { ganttPinchRef.current = null; }}
      >
        <div className="bg-white p-4" style={{ position: 'relative', minHeight: '100%', paddingLeft: 0 }}>
          <div
            style={{ position: 'absolute', left: ganttLabelWidth - 3, top: 0, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 30 }}
            onMouseDown={handleGanttLabelResizeStart}
          />
          <div style={{ position: 'sticky', top: -16, zIndex: 20, background: 'white', marginLeft: 0, marginRight: -16, paddingLeft: 0, paddingRight: 16 }}>
          {(() => {
            const anyVisible = visibleLevels.quarters || visibleLevels.months || visibleLevels.weeks || visibleLevels.days || visibleLevels.hours;
            const resizeHandle = <div style={{ position: 'absolute', right: -3, top: 0, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 20 }} onMouseDown={handleGanttLabelResizeStart} />;
            return (
              <>
                {visibleLevels.quarters && calendarHeaders.quarters?.length > 0 && (
                  <div className="flex mb-0">
                    <div className="gantt-label-cell" style={{ width: ganttLabelWidth, flexShrink: 0, position: 'relative', background: 'white', zIndex: 22 }} />
                    {showStartEndColumnInGantt && <div style={{ width: DATE_COL_W * 2, flexShrink: 0, background: 'white', borderLeft: '1px solid #f1f5f9' }} />}
                    <div className="flex-1 flex border-b-2 border-gray-400">
                      {calendarHeaders.quarters.map((q, i) => (
                        <div key={i} className={`text-sm font-bold text-gray-800 text-center py-2 border-r border-gray-400 ${i % 2 === 0 ? 'bg-blue-50' : 'bg-blue-100'}`} style={{ width: dw * q.span }}>{q.label}</div>
                      ))}
                    </div>
                  </div>
                )}
                {visibleLevels.months && calendarHeaders.months?.length > 0 && (
                  <div className="flex mb-0">
                    <div className="gantt-label-cell" style={{ width: ganttLabelWidth, flexShrink: 0, position: 'relative', background: 'white', zIndex: 22 }} />
                    {showStartEndColumnInGantt && <div style={{ width: DATE_COL_W * 2, flexShrink: 0, background: 'white', borderLeft: '1px solid #f1f5f9' }} />}
                    <div className="flex-1 flex border-b border-gray-400">
                      {calendarHeaders.months.map((m, i) => (
                        <div key={i} className={`text-xs font-semibold text-gray-700 text-center py-1.5 border-r border-gray-300 ${i % 2 === 0 ? 'bg-gray-50' : 'bg-gray-100'}`} style={{ width: dw * m.span }}>{m.label}</div>
                      ))}
                    </div>
                  </div>
                )}
                {visibleLevels.weeks && calendarHeaders.weeks?.length > 0 && (
                  <div className="flex mb-0">
                    <div className="gantt-label-cell" style={{ width: ganttLabelWidth, flexShrink: 0, position: 'relative', background: 'white', zIndex: 22 }} />
                    {showStartEndColumnInGantt && <div style={{ width: DATE_COL_W * 2, flexShrink: 0, background: 'white', borderLeft: '1px solid #f1f5f9' }} />}
                    <div className="flex-1 flex border-b border-gray-300">
                      {calendarHeaders.weeks.map((w, i) => (
                        <div key={i} className={`text-xs text-center text-gray-600 py-1 border-r border-gray-200 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} style={{ width: dw * w.span }}>{w.label}</div>
                      ))}
                    </div>
                  </div>
                )}
                {visibleLevels.days && calendarHeaders.days?.length > 0 && (
                  <div className="flex mb-0">
                    <div className="gantt-label-cell" style={{ width: ganttLabelWidth, flexShrink: 0, position: 'relative', background: 'white', zIndex: 22 }} />
                    {showStartEndColumnInGantt && <div style={{ width: DATE_COL_W * 2, flexShrink: 0, background: 'white', borderLeft: '1px solid #f1f5f9' }} />}
                    <div className="flex-1 flex border-b border-gray-300">
                      {calendarHeaders.days.map((day, i) => (
                        <div key={i} className={`text-xs text-center py-1 border-r border-gray-200 overflow-hidden ${day.isWeekday && !day.isNonWorking ? 'bg-white text-gray-800 font-semibold' : 'bg-gray-100 text-gray-500'}`} style={{ width: dw * day.span }}>
                          <div className="text-[10px] leading-none">{day.dayName}</div>
                          <div>{day.day}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {visibleLevels.hours && calendarHeaders.hours?.length > 0 && (
                  <div className="flex mb-0">
                    <div className="gantt-label-cell" style={{ width: ganttLabelWidth, flexShrink: 0, position: 'relative', background: 'white', zIndex: 22 }} />
                    {showStartEndColumnInGantt && <div style={{ width: DATE_COL_W * 2, flexShrink: 0, background: 'white', borderLeft: '1px solid #f1f5f9' }} />}
                    <div className="flex-1 flex border-b border-gray-300">
                      {calendarHeaders.hours.map((h, i) => (
                        // Left-aligned (not centered): the vertical gridline marking this
                        // hour's own start sits at this cell's LEFT edge (it's the previous
                        // cell's own right border) — a centered label used to read as
                        // sitting mid-span, after that line, which looked like it belonged
                        // to the hour before it rather than the one it's actually labeling.
                        <div key={i} className="text-xs text-left py-1 border-r border-gray-200 bg-white text-gray-700 overflow-hidden" style={{ width: dw * h.span, paddingLeft: 3 }}>{h.label.split(':')[0]}</div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex mb-2 pb-2 border-b-2 border-gray-400">
                  <div className="gantt-label-cell text-sm font-semibold text-right pr-2 flex items-center justify-end" style={{ width: ganttLabelWidth, flexShrink: 0, position: 'relative', background: 'white', zIndex: 22 }}>
                    {!anyVisible ? tr('task') : ''}
                    {resizeHandle}
                  </div>
                  {showStartEndColumnInGantt && (
                    <>
                      <div className="text-center" style={{ width: DATE_COL_W, flexShrink: 0, fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid #f1f5f9' }}>
                        {tr('start')}
                      </div>
                      <div className="text-center" style={{ width: DATE_COL_W, flexShrink: 0, fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid #f1f5f9' }}>
                        {tr('end')}
                      </div>
                    </>
                  )}
                  <div className="flex-1" />
                </div>
              </>
            );
          })()}
          </div>

          {resourceSummaryMode && resourceFilter.length > 0 ? (
            <div ref={resourceRowsContainerRef} style={{ position: 'relative' }}>
              {resourceDragIdx !== null && resourceDropIdx !== null && (
                <div style={{ position: 'absolute', left: 0, right: 0, top: resourceDropIdx * 48 - 2, height: 3, background: '#3b82f6', borderRadius: 2, zIndex: 20, pointerEvents: 'none' }} />
              )}
              {resourceFilter.map((name, idx) => {
                const ranges = getResourceSummaryRanges(name);
                return (
                  <div key={name} className="flex items-center mb-2" style={{ userSelect: 'none', opacity: resourceDragIdx === idx ? 0.4 : 1 }}>
                    <div className="gantt-label-cell text-sm" style={{ display: 'flex', alignItems: 'center', width: ganttLabelWidth, flexShrink: 0, position: 'relative', background: 'white', zIndex: 25, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}>
                      <div
                        onMouseDown={(e) => startResourceSummaryDrag(e, idx)}
                        style={{ width: 18, flexShrink: 0, cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14, lineHeight: 1 }}
                        title={tr('dragToReorder')}
                      >
                        ⠿
                      </div>
                      <div className="truncate" style={{ width: ganttLabelWidth - 18, flexShrink: 0 }}>{name}</div>
                    </div>
                    {renderDateCell(undefined, 'start')}
                  {renderDateCell(undefined, 'end')}
                    <div className="gantt-bars-cell relative" style={{ height: 40, width: renderColCount * dw, flexGrow: 0, flexShrink: 0, overflow: 'hidden' }}>
                      <div className="absolute inset-0 flex items-center">
                        {[...Array(renderColCount)].map((_, i) => (
                          <div key={i} className="border-l border-gray-200" style={{ width: dw, height: 40, background: isNonWorkDisplayCol(i + viewStartColIdx) ? '#f3f4f6' : undefined }} />
                        ))}
                      </div>
                      {ranges.length === 0 ? (
                        <p style={{ position: 'absolute', left: 8, fontSize: 11, color: '#9ca3af', margin: 0 }}>{tr('noMatchingTasks')}</p>
                      ) : ranges.map(([s, e], i) => {
                        const barLeft = (s - viewStartColIdx) * dw;
                        const barWidth = Math.max((e - s) * dw - 2, 4);
                        // Clamp the label to the portion of the bar actually inside the visible
                        // column range. A bar that starts before the view or ends after it centers
                        // its label relative to its own (partly off-screen) box by default, which
                        // this cell's overflow:hidden then clips away entirely — so the duration
                        // never appeared for a task running off either edge of the display range.
                        const visLeft = Math.max(barLeft, 0);
                        const visRight = Math.min(barLeft + barWidth, renderColCount * dw);
                        const visWidth = Math.max(visRight - visLeft, 0);
                        return (
                          <div
                            key={i}
                            className="absolute top-1/2 transform -translate-y-1/2 rounded"
                            style={{ left: barLeft, width: barWidth, height: 24, background: '#489FB5' }}
                          >
                            {visWidth > 0 && (
                              <div
                                className="absolute top-0 bottom-0 flex items-center justify-center overflow-hidden"
                                style={{ left: visLeft - barLeft, width: visWidth }}
                              >
                                <span className="text-white text-xs font-semibold truncate px-1">
                                  {Math.round(convertDurationUnit(e - s, durationUnit, ganttDurationDisplay, hoursPerDay) * 100) / 100} {ganttDurationDisplay === 'hours' ? 'h' : ganttDurationDisplay === 'weeks' ? 'w' : 'd'}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : resourceFilter.length > 1 ? (
            <>
              {resourceFilter.map((name, sectionIdx) => {
                const sectionTasks = sortedTasksAdjusted.filter(t => (t.assignee || '').split(',').map(n => n.trim()).includes(name));
                return (
                  <div key={name} style={{ marginTop: sectionIdx > 0 ? 20 : 0 }}>
                    {/* Split like the task rows below (gantt-label-cell + rest of the row) so the
                        resource name gets the same scroll-follow transform (see the scroll handler
                        near the top of this component) instead of scrolling away with the timeline. */}
                    <div style={{ position: 'relative', height: 28, borderRadius: 6, marginBottom: 6, width: ganttLabelWidth + (showStartEndColumnInGantt ? DATE_COL_W * 2 : 0) + renderColCount * dw, boxSizing: 'border-box', overflow: 'hidden', background: '#e6f4f5' }}>
                      <div
                        className="gantt-label-cell"
                        style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: ganttLabelWidth, zIndex: 22, display: 'flex', alignItems: 'center', paddingLeft: 10, paddingRight: 10, fontSize: 13, fontWeight: 700, color: '#16697A', background: '#e6f4f5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {name}
                      </div>
                    </div>
                    {sectionTasks.length === 0 ? (
                      <p style={{ fontSize: 12, color: '#9ca3af', padding: '4px 10px' }}>{tr('noMatchingTasks')}</p>
                    ) : renderGanttSection(sectionTasks, false, name)}
                  </div>
                );
              })}
            </>
          ) : resourceFilter.length === 1 ? (
            <div>
              <div style={{ position: 'relative', height: 28, borderRadius: 6, marginBottom: 6, width: ganttLabelWidth + (showStartEndColumnInGantt ? DATE_COL_W * 2 : 0) + renderColCount * dw, boxSizing: 'border-box', overflow: 'hidden', background: '#e6f4f5' }}>
                <div
                  className="gantt-label-cell"
                  style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: ganttLabelWidth, zIndex: 22, display: 'flex', alignItems: 'center', paddingLeft: 10, paddingRight: 10, fontSize: 13, fontWeight: 700, color: '#16697A', background: '#e6f4f5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {resourceFilter[0]}
                </div>
              </div>
              {sortedTasksAdjusted.length === 0 ? (
                <p style={{ fontSize: 12, color: '#9ca3af', padding: '4px 10px' }}>{tr('noMatchingTasks')}</p>
              ) : renderGanttSection(sortedTasksAdjusted, true, resourceFilter[0])}
            </div>
          ) : sortedTasksAdjusted.length === 0 ? (
            <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: '24px 0' }}>{tr('noMatchingTasks')}</p>
          ) : renderGanttSection(
              ganttRowTasks,
              // Drag-to-reorder commits against ganttSortedTasksRef (the full,
              // unfiltered order) by index — those indices only line up with what's
              // rendered when nothing is hidden, so disable dragging while "hide
              // empty rows" is actually narrowing the row list, rather than risk
              // reordering the wrong task.
              !(exportOnlyVisibleBars && effectiveTimelineView),
              null, showSubitemsInGantt, showAreaGroupsInGantt, showRegularBoxesInGantt,
              filteredExportEnabled,
            )}
        </div>
      </div>


      {/* Timeline popup (fixed — toolbar has overflow:hidden) */}
      {showTimelinePopup && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setShowTimelinePopup(false)} />
          <div style={{
            position: 'fixed', top: timelinePopupPos.top, left: timelinePopupPos.left, zIndex: 50,
            background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{tr('show')}</div>
            {([
              ['quarters', tr('quarters')],
              ['months',   tr('months')],
              ['weeks',    tr('weeksLevel')],
              ['days',     tr('daysLevel')],
              ['hours',    tr('hoursLevel')],
            ] as [keyof typeof visibleLevels, string][]).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={visibleLevels[key]} onChange={(e) => setVisibleLevels({ ...visibleLevels, [key]: e.target.checked })} className="cursor-pointer" />
                {label}
              </label>
            ))}
          </div>
        </>
      )}

      {/* View popup — display/visibility toggles, moved out of the Timeline popup
          above so it only holds time-level granularity checkboxes. */}
      {showViewPopup && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setShowViewPopup(false)} />
          <div style={{
            position: 'fixed', top: viewPopupPos.top, left: viewPopupPos.left, zIndex: 50,
            background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180,
          }}>
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none font-semibold">
              <input type="checkbox" checked={workHoursOnly} onChange={(e) => onWorkHoursOnlyChange(e.target.checked)} className="cursor-pointer" />
              {tr('hideNonWorkingDay')}
            </label>
            {durationUnit === 'hours' && !fullDaySchedule && (
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none font-semibold">
                <input type="checkbox" checked={hideNonWorkingHours} onChange={(e) => onHideNonWorkingHoursChange(e.target.checked)} className="cursor-pointer" />
                {tr('hideNonWorkingHours')}
              </label>
            )}
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none font-semibold">
              <input type="checkbox" checked={showSubitemsInGantt} onChange={(e) => setShowSubitemsInGantt(e.target.checked)} className="cursor-pointer" />
              {tr('showSubitemsInGantt')}
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none font-semibold">
              <input type="checkbox" checked={showAreaGroupsInGantt} onChange={(e) => setShowAreaGroupsInGantt(e.target.checked)} className="cursor-pointer" />
              {tr('showAreaGroupsInGantt')}
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none font-semibold">
              <input type="checkbox" checked={showRegularBoxesInGantt} onChange={(e) => setShowRegularBoxesInGantt(e.target.checked)} className="cursor-pointer" />
              {tr('showRegularBoxesInGantt')}
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none font-semibold">
              <input type="checkbox" checked={showStartEndColumnInGantt} onChange={(e) => setShowStartEndColumnInGantt(e.target.checked)} className="cursor-pointer" />
              {tr('showStartEndColumnInGantt')}
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none font-semibold" title={tr('hideEmptyGanttRowsHint')}>
              <input type="checkbox" checked={exportOnlyVisibleBars} onChange={(e) => setExportOnlyVisibleBars(e.target.checked)} className="cursor-pointer" />
              {tr('hideEmptyGanttRows')}
            </label>
          </div>
        </>
      )}

      {/* Set timeline dates popup (fixed — toolbar has overflow:hidden) */}
      {showTimelineDatesPopup && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setShowTimelineDatesPopup(false)} />
          <div style={{
            position: 'fixed', top: timelineDatesPopupPos.top, left: timelineDatesPopupPos.left,
            transform: 'translateX(-100%)', zIndex: 50,
            background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 280, maxWidth: 340,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 2 }}>{tr('setTimelineDates')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>{tr('timelineFrom')}</label>
              <input type="date" value={viewStartInput} onChange={e => setViewStartInput(e.target.value)}
                style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>{tr('timelineTo')}</label>
              <input type="date" value={viewEndInput} onChange={e => setViewEndInput(e.target.value)}
                style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
              <button
                onClick={() => { onTimelineViewChange(null); setExportTimelineOverride(null); setShowTimelineDatesPopup(false); }}
                style={{ flex: 1, padding: '6px 0', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, background: 'white', cursor: 'pointer', color: '#6b7280', fontWeight: 600 }}
              >{tr('autoFit')}</button>
              <button
                onClick={() => {
                  const v = viewStartInput || viewEndInput ? { start: viewStartInput, end: viewEndInput } : null;
                  onTimelineViewChange(v);
                  // An explicit manual date entry should win outright over a preset's
                  // saved range, same reasoning as the drag/auto-sort row-order resets.
                  setExportTimelineOverride(null);
                  setShowTimelineDatesPopup(false);
                }}
                style={{ flex: 1, padding: '6px 0', border: 'none', borderRadius: 6, fontSize: 12, background: '#16697A', color: 'white', cursor: 'pointer', fontWeight: 600 }}
              >{tr('done')}</button>
            </div>

            <div style={{ display: 'flex', gap: 6, borderTop: '1px solid #f3f4f6', paddingTop: 10 }}>
              <input
                type="text"
                value={newPresetName}
                onChange={e => setNewPresetName(e.target.value)}
                placeholder={tr('presetNamePlaceholder')}
                style={{ flex: 1, minWidth: 0, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
              />
              <button
                onClick={() => {
                  if (!newPresetName.trim() || !viewStartInput || !viewEndInput) return;
                  onAddTimelinePreset(newPresetName.trim(), viewStartInput, viewEndInput);
                  setNewPresetName('');
                }}
                disabled={!newPresetName.trim() || !viewStartInput || !viewEndInput}
                style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, background: 'white', cursor: 'pointer', color: '#374151', fontWeight: 600, flexShrink: 0, opacity: (!newPresetName.trim() || !viewStartInput || !viewEndInput) ? 0.5 : 1 }}
              >{tr('saveAsPreset')}</button>
            </div>

            {timelinePresets.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{tr('savedTimelines')}</div>
                {timelinePresets.map(p => (
                  <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '8px', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="text"
                        value={p.name}
                        onChange={e => onUpdateTimelinePreset(p.id, { name: e.target.value })}
                        style={{ flex: 1, minWidth: 0, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, fontWeight: 600 }}
                      />
                      <button
                        onClick={() => onDeleteTimelinePreset(p.id)}
                        title={tr('delete')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2, flexShrink: 0 }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="date" value={p.start} onChange={e => onUpdateTimelinePreset(p.id, { start: e.target.value })}
                        style={{ flex: 1, minWidth: 0, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11 }} />
                      <input type="date" value={p.end} onChange={e => onUpdateTimelinePreset(p.id, { end: e.target.value })}
                        style={{ flex: 1, minWidth: 0, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11 }} />
                    </div>
                    <button
                      onClick={() => {
                        onTimelineViewChange({ start: p.start, end: p.end });
                        setExportTimelineOverride(null);
                        setShowTimelineDatesPopup(false);
                      }}
                      style={{ padding: '5px 0', border: 'none', borderRadius: 5, fontSize: 12, background: '#16697A', color: 'white', cursor: 'pointer', fontWeight: 600 }}
                    >{tr('applyPreset')}</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Filtered Export popup (fixed — toolbar has overflow:hidden) */}
      {showFilteredExportPopup && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setShowFilteredExportPopup(false)} />
          <div style={{
            position: 'fixed', top: filteredExportPopupPos.top, left: filteredExportPopupPos.left, zIndex: 50,
            background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 280, maxWidth: 340,
          }}>
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none font-semibold">
              <input
                type="checkbox"
                checked={filteredExportEnabled}
                onChange={(e) => {
                  setFilteredExportEnabled(e.target.checked);
                  // Drag-to-reorder is disabled while "Hide empty rows" narrows the
                  // row list (see renderGanttSection's own comment on this, a bit
                  // further down) — so turning this mode on would otherwise silently
                  // hide the dragging it's meant to enable. Only act on the way in;
                  // don't re-enable "Hide empty rows" on the way out, since the user
                  // may have wanted it off anyway.
                  if (e.target.checked) setExportOnlyVisibleBars(false);
                }}
                className="cursor-pointer"
              />
              {tr('switchViewToSelectRows')}
            </label>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{tr('onlyExportSelectedRowsHint')}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{tr('switchViewToSelectRowsHideEmptyNote')}</div>

            {filteredExportEnabled && (
              <>
                <div style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>
                  {tr('rowsSelectedCount', { count: exportSelectedBoxIds.length, total: ganttTasks.length })}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => { setExportSelectedBoxIds(ganttTasks.map(t => t.id)); setExportOrderOverride(null); setExportTimelineOverride(null); }}
                    style={{ flex: 1, padding: '6px 0', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, background: 'white', cursor: 'pointer', color: '#374151', fontWeight: 600 }}
                  >{tr('selectAllRows')}</button>
                  <button
                    onClick={() => { setExportSelectedBoxIds([]); setExportOrderOverride(null); setExportTimelineOverride(null); }}
                    style={{ flex: 1, padding: '6px 0', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, background: 'white', cursor: 'pointer', color: '#374151', fontWeight: 600 }}
                  >{tr('selectNoRows')}</button>
                </div>

                <div style={{ display: 'flex', gap: 6, borderTop: '1px solid #f3f4f6', paddingTop: 10 }}>
                  <input
                    type="text"
                    value={newExportPresetName}
                    onChange={e => setNewExportPresetName(e.target.value)}
                    placeholder={tr('presetNamePlaceholder')}
                    style={{ flex: 1, minWidth: 0, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
                  />
                  <button
                    onClick={() => {
                      if (!newExportPresetName.trim()) return;
                      // Save in the order these rows actually render in right now (the
                      // live drag order, or a previously-applied preset's own order) —
                      // not checkbox-click order — so re-applying this preset later can
                      // restore that same row order without needing to re-drag.
                      const orderedIds = ganttSortedTasksRef.current.map(t => t.id).filter(id => exportSelectedBoxIds.includes(id));
                      // Compute an explicit range spanning every selected row's bar —
                      // not "whatever the active Set Timeline Dates crop happens to be,
                      // if it's wide enough" (that was the previous attempt: it saved
                      // nothing at all whenever the active crop fell short, silently
                      // falling back to whatever the LIVE SHARED timelineView happens to
                      // be at the moment someone else applies the preset — meaning the
                      // exact same "narrow leftover crop clips my rows" bug could still
                      // resurface later even for a preset saved correctly today, since
                      // "no override" isn't the same as "wide enough, forever"). Always
                      // deriving the range directly from the selection's own real dates
                      // makes the preset fully self-contained: applying it always shows
                      // every selected row's bar, regardless of whatever timeline crop
                      // anyone else currently has active.
                      const dayIdxToIso = (col: number): string => {
                        const dayIdx = durationUnit === 'hours' ? Math.floor(col / displayHoursPerDay) : col;
                        const d = displayColList[Math.max(0, Math.min(dayIdx, displayColList.length - 1))];
                        return toDs(d);
                      };
                      let minDispStart = Infinity, maxDispEnd = -Infinity;
                      for (const id of exportSelectedBoxIds) {
                        const t = sortedTasksAdjusted.find(tt => tt.id === id);
                        if (!t) continue;
                        const dispStart = toDisplayPos(t.start);
                        const dispEnd = t.duration > 0 ? toDisplayEndPos(t.start + t.duration) : dispStart;
                        if (dispStart < minDispStart) minDispStart = dispStart;
                        if (dispEnd > maxDispEnd) maxDispEnd = dispEnd;
                      }
                      const timelineRange = Number.isFinite(minDispStart) && Number.isFinite(maxDispEnd)
                        ? { start: dayIdxToIso(minDispStart), end: dayIdxToIso(maxDispEnd) }
                        : null;
                      onAddExportRowPreset(newExportPresetName.trim(), orderedIds, timelineRange);
                      setNewExportPresetName('');
                    }}
                    disabled={!newExportPresetName.trim()}
                    style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, background: 'white', cursor: 'pointer', color: '#374151', fontWeight: 600, flexShrink: 0, opacity: !newExportPresetName.trim() ? 0.5 : 1 }}
                  >{tr('saveAsPreset')}</button>
                </div>

                {exportRowPresets.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{tr('savedExportSelections')}</div>
                    {exportRowPresets.map(p => (
                      <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '8px', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input
                            type="text"
                            value={p.name}
                            onChange={e => onUpdateExportRowPreset(p.id, { name: e.target.value })}
                            style={{ flex: 1, minWidth: 0, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, fontWeight: 600 }}
                          />
                          <button
                            onClick={() => onDeleteExportRowPreset(p.id)}
                            title={tr('delete')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2, flexShrink: 0 }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            setExportSelectedBoxIds(p.boxIds);
                            setExportOrderOverride(p.boxIds);
                            setExportTimelineOverride(p.timelineStart && p.timelineEnd ? { start: p.timelineStart, end: p.timelineEnd } : null);
                          }}
                          style={{ padding: '5px 0', border: 'none', borderRadius: 5, fontSize: 12, background: '#16697A', color: 'white', cursor: 'pointer', fontWeight: 600 }}
                        >{tr('useThisSavedPreset')}</button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Resource filter popup (fixed — toolbar has overflow:hidden) */}
      {showResourceFilterPopup && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setShowResourceFilterPopup(false)} />
          <div style={{
            position: 'fixed', top: resourceFilterPopupPos.top, left: resourceFilterPopupPos.left, zIndex: 50,
            background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180, maxHeight: 280, overflowY: 'auto',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{tr('filterByResource')}</div>
            {resourceFilter.length < resources.length && (
              <button
                onClick={() => setResourceFilter(resources.map(r => r.name))}
                style={{ marginBottom: 4, padding: '4px 0', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11, background: 'white', cursor: 'pointer', color: '#6b7280', fontWeight: 600 }}
              >{tr('selectAllResources')}</button>
            )}
            {resourceFilter.length > 0 && (
              <button
                onClick={() => setResourceFilter([])}
                style={{ marginBottom: 4, padding: '4px 0', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11, background: 'white', cursor: 'pointer', color: '#6b7280', fontWeight: 600 }}
              >{tr('clearFilter')}</button>
            )}
            {resourceFilter.length > 0 && (
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none" style={{ marginBottom: 4, paddingBottom: 6, borderBottom: '1px solid #f3f4f6' }}>
                <input
                  type="checkbox"
                  checked={resourceSummaryMode}
                  onChange={(e) => setResourceSummaryMode(e.target.checked)}
                  className="cursor-pointer"
                />
                {tr('resourceSummaryMode')}
              </label>
            )}
            {resourceGroups.map(([team, group]) => {
              const groupNames = group.map(r => r.name);
              const allChecked = groupNames.every(n => resourceFilter.includes(n));
              const someChecked = !allChecked && groupNames.some(n => resourceFilter.includes(n));
              return (
              <div key={team || '__none__'}>
                {resourceGroups.length > 1 && (
                  <label
                    className="flex items-center gap-2 cursor-pointer select-none"
                    style={{ fontSize: 10, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 4, marginBottom: 2 }}
                  >
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = someChecked; }}
                      onChange={(e) => {
                        setResourceFilter(e.target.checked
                          ? [...resourceFilter, ...groupNames.filter(n => !resourceFilter.includes(n))]
                          : resourceFilter.filter(n => !groupNames.includes(n)));
                      }}
                      className="cursor-pointer"
                    />
                    {team || tr('noTeam')}
                  </label>
                )}
                {group.map((r, i) => (
                  <label key={i} className="flex items-center gap-2 text-xs cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={resourceFilter.includes(r.name)}
                      onChange={(e) => setResourceFilter(e.target.checked ? [...resourceFilter, r.name] : resourceFilter.filter(n => n !== r.name))}
                      className="cursor-pointer"
                    />
                    <span style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: departments.find(d => d.name === r.category)?.color || 'transparent' }} />
                    {r.name}
                  </label>
                ))}
              </div>
              );
            })}
          </div>
        </>
      )}

      {/* Category filter popup (fixed — toolbar has overflow:hidden) — same shape as
          the resource filter popup above, minus team-grouping/summary-mode (a
          Department has neither concept). */}
      {showCategoryFilterPopup && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setShowCategoryFilterPopup(false)} />
          <div style={{
            position: 'fixed', top: categoryFilterPopupPos.top, left: categoryFilterPopupPos.left, zIndex: 50,
            background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180, maxHeight: 280, overflowY: 'auto',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{tr('filterByCategory')}</div>
            {categoryFilter.length < departments.length && (
              <button
                onClick={() => setCategoryFilter(departments.map(d => d.name))}
                style={{ marginBottom: 4, padding: '4px 0', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11, background: 'white', cursor: 'pointer', color: '#6b7280', fontWeight: 600 }}
              >{tr('selectAllResources')}</button>
            )}
            {categoryFilter.length > 0 && (
              <button
                onClick={() => setCategoryFilter([])}
                style={{ marginBottom: 4, padding: '4px 0', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11, background: 'white', cursor: 'pointer', color: '#6b7280', fontWeight: 600 }}
              >{tr('clearFilter')}</button>
            )}
            {departments.map((d, i) => (
              <label key={i} className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={categoryFilter.includes(d.name)}
                  onChange={(e) => setCategoryFilter(e.target.checked ? [...categoryFilter, d.name] : categoryFilter.filter(n => n !== d.name))}
                  className="cursor-pointer"
                />
                <span style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0, display: 'inline-block' }} />
                {d.name}
              </label>
            ))}
          </div>
        </>
      )}

      {/* Gantt export popup */}
      {showGanttExportMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 399 }} onClick={() => setShowGanttExportMenu(false)} />
          <div style={{ position: 'fixed', top: ganttExportMenuPos.top, left: ganttExportMenuPos.left, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', zIndex: 400, padding: '6px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 100 }}>
            <button onClick={() => { exportGanttAs('png'); setShowGanttExportMenu(false); }} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', textAlign: 'left' }}>PNG</button>
            <button onClick={() => { exportGanttAs('svg'); setShowGanttExportMenu(false); }} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', textAlign: 'left' }}>SVG</button>
          </div>
        </>
      )}

      {/* Bar right-click context menu */}
      {barContextMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 399 }} onClick={() => setBarContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setBarContextMenu(null); }} />
          <div style={{ position: 'fixed', top: barContextMenu.top, left: barContextMenu.left, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', zIndex: 400, padding: '6px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
            <button
              onClick={() => { onShowInFlowplan(barContextMenu.taskId); setBarContextMenu(null); }}
              style={{ background: 'none', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', textAlign: 'left' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              {tr('showInFlowplan')}
            </button>
            {barContextMenu.resource && (
              <button
                onClick={() => { setEditingResourceCell({ taskId: barContextMenu.taskId, resource: barContextMenu.resource! }); setBarContextMenu(null); }}
                style={{ background: 'none', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', textAlign: 'left' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              >
                {tr('selectSpecificResourceTimes')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
